import {
    setCurrencyFormat,
    generate_UUID,
    numFormatObj,
    getArrayItem,
} from 'src/utils/util';
import { normalizeToLocalCalendarDate } from 'src/utils/dateUtils';
import {
    checkIfValidQuantities,
    getTotalExtendedPrice,
    getTotalQuantity,
    flattenLotSerialArrayOfArrays,
    displayCheckbox,
} from './eventHandlerUtil';
import i18n from 'src/i18n.js';
const t = (key) => i18n.t(key);

export const invalidProductMsg = () =>
    t('reportUsage.procedure.unidSurgery.SurgeryOrReport.ProductNotfound');
export const invalidBundleItemMsg = () =>
    t('productValidationMessages.invalidBundleItemMsg');
export const invalidSPSitemMsg = () =>
    t('productValidationMessages.invalidSPSitemMsg');
export const emptyBundleMsg = () =>
    t('reportUsage.itemTableErrorMessages.Bundle');
export const productIssueErrMsg = t(
    'productValidationMessages.ProductissueCare'
);
export const invalidUnidRodItemQtyMsg = t(
    'reportUsage.itemTableErrorMessages.UnidQty'
);
export const invalidSerialNumberMsg = () =>
    t('equity.common.utils.invalidSerial');
export const invalidLotNumberMsg = () => t('equity.common.utils.invalidLot');
export const serialNumberReqdMsg = () =>
    t('equity.common.utils.serialRequired');
export const lotNumberReqdMsg = () => t('equity.common.utils.lotRequired');
export const multipleImageWarningMsg = () =>
    t('productValidationMessages.multipleImageWarningMsg');
export const noPriceErrorMsg = t('productValidationMessages.noPriceErrorMsg');
export const ACCOUNT_OWNED_REQUIRED_MSG = 'Item must be Account-owned.';

const LINKED_UNID_ORDER_STATUS_CODES = new Set([
    '11902',
    '11903',
    '11904',
    '11905',
    '11906',
    '11907',
    '11909',
]);

const isS33Item = (item) =>
    (item?.cfn || item?.productCFN || '').toUpperCase().startsWith('S33');

export const normalizeDienFeeCodes = (dienFeeTypeItemNumbers) => {
    if (Array.isArray(dienFeeTypeItemNumbers)) {
        return dienFeeTypeItemNumbers.filter(Boolean);
    }
    if (
        typeof dienFeeTypeItemNumbers === 'string' &&
        dienFeeTypeItemNumbers.length > 0
    ) {
        return dienFeeTypeItemNumbers
            .split(',')
            .map((code) => code.trim())
            .filter(Boolean);
    }
    return [];
};

// Checks whether a FEE (S33*) item was part of the original Gateway order request.
export const isFeeItemInGatewayOrder = (cfn, usageDetails) => {
    const normalizedCfn = (cfn || '').toUpperCase();
    if (!normalizedCfn) return false;

    const dienFeeCodes = normalizeDienFeeCodes(
        usageDetails?.dienFeeTypeItemNumbers
    ).map((code) => code.toUpperCase());
    if (dienFeeCodes.includes(normalizedCfn)) {
        return true;
    }

    return !!usageDetails?.products?.some(
        (product) => (product?.productCFN || '').toUpperCase() === normalizedCfn
    );
};

export const isFeeItemAllowedInUnidRodBundle = (
    cfn,
    usageDetails,
    bundleDescription
) => {
    const normalizedDescription = bundleDescription?.toUpperCase();
    const isUnidRodBundle = Boolean(
        normalizedDescription?.includes('UNID') &&
            normalizedDescription?.includes('ROD')
    );

    return Boolean(
        isUnidRodBundle &&
            (isFeeItemInGatewayOrder(cfn, usageDetails) || usageDetails?.unidId)
    );
};

export const isProductCfnInList = (cfn, products) => {
    const normalizedCfn = (cfn || '').toUpperCase();
    if (!normalizedCfn || !Array.isArray(products)) return false;

    return products.some((product) => {
        const productCfn = (product?.cfn || product?.productCFN || '').toUpperCase();
        return (
            productCfn === normalizedCfn ||
            isProductCfnInList(normalizedCfn, product?.children) ||
            isProductCfnInList(normalizedCfn, product?.bundleItem)
        );
    });
};

const getLotSerialValidationData = (item, validatedProduct) => {
    const lotSerialData = validatedProduct?.lotSerialData || [];

    return isS33Item(item)
        ? lotSerialData.filter(
              ({ statusCode }) => !LINKED_UNID_ORDER_STATUS_CODES.has(statusCode)
          )
        : lotSerialData;
};

export const getUniqueKey = () => {
    return new Date().getTime();
};

export const getModifiedProcedureResponse = (procedureData) => {
    return Object.fromEntries(
        Object.entries(procedureData).map((entry) => [
            entry[0],
            entry[1] instanceof Array
                ? entry[1].map((x) => ({
                      ...x,
                      description:
                          x.description === 'MazorX Robot Used'
                              ? 'MazorX Robot Used'
                              : x.description,
                      isSelected: null,
                  }))
                : entry[1],
        ])
    );
};
export const getModifiedBundleChildItems = (
    existingItem,
    pricedItemsArr,
    bundleHeadersArr
) => {
    return existingItem?.bundleItem?.length > 0
        ? existingItem?.bundleItem?.map((child) => {
              let updatedChildItemObj = { ...child };
              const pricedChildItemIdx = pricedItemsArr
                  .map((x) => x.productCFN)
                  .findIndex((y) => y === child.productCFN);
              if (pricedChildItemIdx >= 0) {
                  updatedChildItemObj = {
                      ...pricedItemsArr[pricedChildItemIdx],
                      productCFN: child.productCFN,
                      isUNiDPlanFeeItem: child.isUNiDPlanFeeItem,
                      isReplenishmentRequired: child.isReplenishmentRequired,
                      cfn: child.productCFN,
                      lotSerial: child.lotSerial,
                      lotSerialNumber: child.lotSerialNumber
                          ? child.lotSerialNumber
                          : '',
                      salesOrg: pricedItemsArr[pricedChildItemIdx].salesOrg,
                      parentCFN: existingItem.cfn,
                      parentId: existingItem.unqId,
                      isBundleChild: child.isBundleChild,
                      quantity: child.quantity,
                      usedQuantity: child.quantity,
                      isReplenish: false,
                      isAdditionalCartChild: child.isAdditionalCartChild,
                      id: child.id ? child.id : generate_UUID(),
                      unqId: child.unqId ? child.unqId : generate_UUID(),
                      isPriced: true,
                  };
                  updatedChildItemObj.basePrice = null;
                  if (pricedItemsArr[pricedChildItemIdx].salesOrg === 'S030') {
                      updatedChildItemObj.isInvalidItemMsg = t(
                          'equity.common.utils.tissueItems'
                      );
                  } else if (
                      pricedItemsArr[pricedChildItemIdx].exclusionStatus ===
                      'Item number not found.'
                  ) {
                      updatedChildItemObj.isInvalidItemMsg =
                          invalidProductMsg();
                  } else if (pricedItemsArr[pricedChildItemIdx].isSet) {
                      updatedChildItemObj.isInvalidItemMsg = t(
                          'equity.common.utils.setsNotAllowed'
                      );
                  } else if (
                      pricedItemsArr[pricedChildItemIdx].exclusionStatus &&
                      pricedItemsArr[pricedChildItemIdx].exclusionStatus !==
                          noPriceErrorMsg
                  ) {
                      updatedChildItemObj.isInvalidItemMsg =
                          pricedItemsArr[pricedChildItemIdx].exclusionStatus;
                  } else {
                      updatedChildItemObj.isInvalidItemMsg = null;
                  }
              } else if (bundleHeadersArr.includes(child.cfn)) {
                  updatedChildItemObj.isInvalidItemMsg = t(
                      'equity.common.utils.bundleItem'
                  );
              } else if (child.cfn.toUpperCase().startsWith('SPS')) {
                  updatedChildItemObj.isInvalidItemMsg = invalidSPSitemMsg();
              } else {
                  updatedChildItemObj.isInvalidItemMsg = invalidProductMsg();
              }
              return updatedChildItemObj;
          })
        : existingItem.children.map((child) => {
              let updatedChildItemObj = { ...child };
              const pricedChildItemIdx = pricedItemsArr
                  .map((x) => x.productCFN)
                  .findIndex((y) => y === child.cfn);
              if (pricedChildItemIdx >= 0) {
                  updatedChildItemObj = {
                      ...pricedItemsArr[pricedChildItemIdx],
                      cfn: child.cfn,
                      lotSerial: child.lotSerial,
                      salesOrg: pricedItemsArr[pricedChildItemIdx].salesOrg,
                      parentCFN: existingItem.cfn,
                      parentId: existingItem.unqId,
                  };
                  updatedChildItemObj.basePrice = null;
                  if (pricedItemsArr[pricedChildItemIdx].salesOrg === 'S030') {
                      updatedChildItemObj.isInvalidItemMsg = t(
                          'equity.common.utils.tissueItems'
                      );
                  } else if (
                      pricedItemsArr[pricedChildItemIdx].exclusionStatus ===
                      'Item number not found.'
                  ) {
                      updatedChildItemObj.isInvalidItemMsg =
                          invalidProductMsg();
                  } else if (pricedItemsArr[pricedChildItemIdx].isSet) {
                      updatedChildItemObj.isInvalidItemMsg = t(
                          'equity.common.utils.setsNotAllowed'
                      );
                  } else if (
                      pricedItemsArr[pricedChildItemIdx].exclusionStatus &&
                      pricedItemsArr[pricedChildItemIdx].exclusionStatus !==
                          noPriceErrorMsg
                  ) {
                      updatedChildItemObj.isInvalidItemMsg =
                          pricedItemsArr[pricedChildItemIdx].exclusionStatus;
                  } else {
                      updatedChildItemObj.isInvalidItemMsg = null;
                  }
              } else if (bundleHeadersArr.includes(child.cfn)) {
                  updatedChildItemObj.isInvalidItemMsg = t(
                      'equity.common.utils.bundleItem'
                  );
              } else if (child.cfn.toUpperCase().startsWith('SPS')) {
                  updatedChildItemObj.isInvalidItemMsg = invalidSPSitemMsg();
              } else {
                  updatedChildItemObj.isInvalidItemMsg = invalidProductMsg();
              }
              return updatedChildItemObj;
          });
};

export const getModifiedItemTableData = (
    existingItemsArr,
    pricedItemsArr,
    bundleHeadersArr,
    isFromReportPaymentScreen,
    deliveredUnidRodItems,
    myCaseResourceType
) => {
    const itemTableDataWithPrices = existingItemsArr.map((existingItem) => {
        let updatedItemObj = { ...existingItem };
        if (existingItem.scannedGTIN) {
            updatedItemObj.scannedGTIN = existingItem.scannedGTIN;
        }
        if (
            myCaseResourceType !== 'reportPayment' &&
            isFromReportPaymentScreen &&
            existingItem.isAdditionalCartEntry &&
            (existingItem.cfn.toUpperCase().startsWith('M2') ||
                existingItem.cfn.toUpperCase().startsWith('M1') ||
                existingItem.cfn.toUpperCase().startsWith('BUN') ||
                existingItem.cfn.toUpperCase().startsWith('KEN')) &&
            bundleHeadersArr.includes(existingItem.cfn.toUpperCase())
        ) {
            updatedItemObj.isInvalidItemMsg = t(
                'equity.common.utils.bundleAdded'
            );
        } else {
            const pricedItemIdx = pricedItemsArr
                .map((x) => x.productCFN)
                .findIndex((y) => y === existingItem.cfn);
            const unidItem = deliveredUnidRodItems?.filter(
                (uItem) =>
                    uItem.productCFN === existingItem.cfn &&
                    uItem.isUnidRodItem &&
                    uItem.itemBatchAndQty &&
                    uItem.itemBatchAndQty.length > 0
            );
            if (pricedItemIdx >= 0) {
                if (
                    isFromReportPaymentScreen &&
                    existingItem.isAdditionalCartEntry
                ) {
                    updatedItemObj = {
                        ...pricedItemsArr[pricedItemIdx],
                        isDienFeeItem: existingItem.isDienFeeItem || false,
                        cfn: existingItem.cfn,
                        lotSerialNumber: existingItem.lotSerialNumber
                            ? existingItem.lotSerialNumber
                            : '',
                        accountOwned: existingItem.accountOwned
                            ? existingItem.accountOwned
                            : false,
                        salesOrg: pricedItemsArr[pricedItemIdx].salesOrg,
                        unitPrice: pricedItemsArr[pricedItemIdx].basePrice,
                        lotSerialUnknownApplicable: pricedItemsArr[pricedItemIdx].lotSerialUnknownApplicable,
                        scannedGTIN: existingItem.scannedGTIN
                            ? existingItem.scannedGTIN
                            : '',
                        usedQuantity:
                            pricedItemsArr[pricedItemIdx].salesOrg === 'S030'||  pricedItemsArr?.[pricedItemIdx]?.showCheckBox
                                ? existingItem.usedQuantity === true ||
                                  parseInt(existingItem.usedQuantity) > 0
                                : existingItem.usedQuantity === true
                                ? 1
                                : existingItem.usedQuantity === false
                                ? undefined
                                        : existingItem.usedQuantity,
                        lotSerialUnknown:existingItem?.lotSerialUnknown,
                        otherQuantity:
                            pricedItemsArr[pricedItemIdx].salesOrg === 'S030'||pricedItemsArr?.[pricedItemIdx]?.showCheckBox
                                ? existingItem.otherQuantity === true ||
                                  (parseInt(existingItem.usedQuantity) > 0
                                      ? false
                                      : parseInt(existingItem.otherQuantity) >
                                        0)
                                : existingItem.otherQuantity === true
                                ? 1
                                : existingItem.otherQuantity === false
                                ? undefined
                               :existingItem.otherQuantity,
                        lotSerial: existingItem.lotSerial
                            ? existingItem.lotSerial
                            : [],
                        isAdditionalCartEntry:
                            existingItem.isAdditionalCartEntry,
                        isReplenishmentRequired: existingItem?.isReplenishmentRequired,                            
                        poImageErrorMsg: existingItem.poImageErrorMsg,
                        isPoImageError: existingItem.isPoImageError,
                        isBundleItem: existingItem.isBundleItem,
                        isBundleFromAddItem: existingItem.isBundleFromAddItem,
                        bundleItem: existingItem.bundleItem,
                        bundleHeaderPosition: existingItem.bundleHeaderPosition,
                        id: existingItem.id ? existingItem.id : generate_UUID(),
                        unqId: existingItem.unqId
                            ? existingItem.unqId
                            : generate_UUID(),
                        isPriced: true,
                        isOverrideApplied: existingItem.isOverrideApplied,
                        isSplitLotSerialItem:
                            existingItem.isSplitLotSerialItem,
                        splitQtyType: existingItem.splitQtyType,
                    };
                    if (existingItem.isUnidRodItem || unidItem?.length > 0) {
                        updatedItemObj.isUnidRodItem = true;
                        updatedItemObj.batchQtyObjArr =
                            existingItem.batchQtyObjArr ||
                            (unidItem && getArrayItem(unidItem)?.itemBatchAndQty);
                        if (existingItem.isInvalidUnidItemQuantity) {
                            updatedItemObj.isInvalidUnidItemQuantity =
                                existingItem.isInvalidUnidItemQuantity;
                        }
                    }

                    if (existingItem.adjustedUsedQtyPrice) {
                        updatedItemObj.adjustedUsedQtyPrice =
                            existingItem.adjustedUsedQtyPrice;
                    }
                    if (existingItem.adjustedOtherQtyPrice) {
                        updatedItemObj.adjustedOtherQtyPrice =
                            existingItem.adjustedOtherQtyPrice;
                    }
                    const overrideUnitPrice =
                        existingItem?.overrideUnitPrice?existingItem?.overrideUnitPrice: existingItem.usedQuantity? existingItem.adjustedUsedQtyPrice
                        : existingItem.adjustedOtherQtyPrice;
                    if (overrideUnitPrice) {
                        updatedItemObj.overrideUnitPrice = overrideUnitPrice;
                        updatedItemObj.isPriceUpdated =
                            existingItem.isPriceOverrideDoneInUsage
                                ? getPriceInNumberDataType(
                                      existingItem.overriddenPrice
                                  ) !==
                                  getPriceInNumberDataType(overrideUnitPrice)
                                : true;
                    } else {
                        updatedItemObj.overrideUnitPrice =
                            existingItem.isPriceOverrideDoneInUsage
                                ? existingItem.adjustedUsedQtyPrice === null ||
                                  existingItem.adjustedOtherQtyPrice === null
                                    ? undefined
                                    : existingItem.overriddenPrice
                                : undefined;
                        updatedItemObj.isPriceUpdated =
                            existingItem.isPriceOverrideDoneInUsage
                                ? true
                                : false;
                    }
                    if (existingItem.isInvalidQtyMsg) {
                        updatedItemObj.isInvalidQtyMsg =
                            existingItem.isInvalidQtyMsg;
                    }
                    if (existingItem.isInvalidLotSerialMsg) {
                        updatedItemObj.isInvalidLotSerialMsg =
                            existingItem.isInvalidLotSerialMsg;
                    }

                    //cannot have both the value in object
                    // if (existingItem.isAdditionalCartEntry) {
                    //     delete updatedItemObj.otherQuantity;
                    // }

                    if (
                        pricedItemsArr[pricedItemIdx].exclusionStatus &&
                        pricedItemsArr[pricedItemIdx].exclusionStatus !==
                            noPriceErrorMsg
                    ) {
                        updatedItemObj.isInvalidItemMsg =
                            pricedItemsArr[pricedItemIdx].exclusionStatus ===
                            'Item number not found.'
                                ? invalidProductMsg()
                                : pricedItemsArr[pricedItemIdx].exclusionStatus;
                    }
                    if (existingItem.payment) {
                        updatedItemObj.payment = existingItem.payment;
                    }
                    if (existingItem.isBundleItem) {
                        updatedItemObj.isBundleItem = existingItem.isBundleItem;
                        updatedItemObj.bundleItem = existingItem.bundleItem;
                    }
                    if (
                        bundleHeadersArr &&
                        bundleHeadersArr.includes(
                            existingItem.cfn.toUpperCase()
                        )
                    ) {
                        if (
                            existingItem.bundleItem &&
                            existingItem.bundleItem.length > 0
                        ) {
                            // bundle child items validation
                            updatedItemObj.bundleItem =
                                getModifiedBundleChildItems(
                                    existingItem,
                                    pricedItemsArr,
                                    bundleHeadersArr
                                );
                        } else {
                            updatedItemObj.noItemInBundleErr = emptyBundleMsg();
                        }
                    }
                } else if (
                    isFromReportPaymentScreen &&
                    !existingItem.isAdditionalCartEntry
                ) {
                    return existingItem;
                } else {
                    updatedItemObj = {
                        ...pricedItemsArr[pricedItemIdx],
                        isDienFeeItem: existingItem.isDienFeeItem || false,
                        unqId: existingItem.unqId,
                        cfn: existingItem.cfn,
                        scannedGTIN: existingItem.scannedGTIN
                            ? existingItem.scannedGTIN
                            : '',
                        lotSerialUnknownApplicable: pricedItemsArr[pricedItemIdx].lotSerialUnknownApplicable,
                        lotSerial:
                            pricedItemsArr[pricedItemIdx].salesOrg === 'S030'|| pricedItemsArr[pricedItemIdx].showCheckBox 
                                ? existingItem.lotSerial.map((lotItem) => ({
                                      ...lotItem,
                                      used:
                                          lotItem.used === true ||
                                          parseInt(lotItem.used) > 0,
                                      other:
                                          lotItem.other === true ||
                                          (parseInt(lotItem.used) > 0
                                              ? false
                                              : parseInt(lotItem.other) > 0),
                                  }))
                                : existingItem.lotSerial.map((lotItem) => ({
                                      ...lotItem,
                                      used:
                                          lotItem.used === true
                                              ? 1
                                              : lotItem.used === false
                                              ? undefined
                                              : lotItem.used,
                                      other:
                                          lotItem.other === true
                                              ? 1
                                              : lotItem.other === false
                                              ? undefined
                                              : lotItem.other,
                                  })),
                        accountOwned: existingItem.accountOwned
                            ? existingItem.accountOwned
                            : false,
                        isAdditionalCartEntry:
                            existingItem?.isAdditionalCartEntry
                                ? existingItem.isAdditionalCartEntry
                                : false,
                        isBundleExpanded: existingItem.isBundleExpanded,
                        isOverrideApplied: existingItem.isOverrideApplied,
                    };
                    if (existingItem.payment) {
                        updatedItemObj.payment = existingItem.payment;
                    }
                    if (existingItem.isUnidRodItem || unidItem?.length > 0) {
                        updatedItemObj.isUnidRodItem = true;
                        updatedItemObj.batchQtyObjArr =
                            existingItem.batchQtyObjArr ||
                            (unidItem && getArrayItem(unidItem)?.itemBatchAndQty);
                        if (existingItem.isInvalidUnidItemQuantity) {
                            updatedItemObj.isInvalidUnidItemQuantity =
                                existingItem.isInvalidUnidItemQuantity;
                        }
                    }

                    if (existingItem.isBundleItem) {
                        updatedItemObj.isBundleItem = existingItem.isBundleItem;
                        updatedItemObj.children = existingItem.children;
                    }

                    if (
                        pricedItemsArr[pricedItemIdx].exclusionStatus &&
                        pricedItemsArr[pricedItemIdx].exclusionStatus !==
                            noPriceErrorMsg
                    ) {
                        updatedItemObj.isInvalidItemMsg =
                            pricedItemsArr[pricedItemIdx].exclusionStatus ===
                            'Item number not found.'
                                ? invalidProductMsg()
                                : pricedItemsArr[pricedItemIdx].exclusionStatus;
                    } else {
                        updatedItemObj.isInvalidItemMsg = null;
                        if (
                            bundleHeadersArr &&
                            bundleHeadersArr.includes(
                                existingItem.cfn.toUpperCase()
                            )
                        ) {
                            if (
                                existingItem.children &&
                                existingItem.children.length > 0
                            ) {
                                // bundle child items validation
                                updatedItemObj.children =
                                    getModifiedBundleChildItems(
                                        existingItem,
                                        pricedItemsArr,
                                        bundleHeadersArr
                                    );
                            } else {
                                updatedItemObj.noItemInBundleErr =
                                    emptyBundleMsg();
                            }
                        }
                        if (existingItem.adjustedUsedQtyPrice) {
                            updatedItemObj.adjustedUsedQtyPrice =
                                existingItem.adjustedUsedQtyPrice;
                        }
                        if (existingItem.adjustedOtherQtyPrice) {
                            updatedItemObj.adjustedOtherQtyPrice =
                                existingItem.adjustedOtherQtyPrice;
                        }
                        updatedItemObj.extPrice = getTotalExtendedPrice(
                            pricedItemsArr[pricedItemIdx].basePrice
                                ? pricedItemsArr[pricedItemIdx].basePrice
                                : '',
                            updatedItemObj.lotSerial,
                            'total'
                        );
                        updatedItemObj.otherQtyExtPrice = getTotalExtendedPrice(
                            pricedItemsArr[pricedItemIdx].basePrice
                                ? pricedItemsArr[pricedItemIdx].basePrice
                                : '',
                            updatedItemObj.lotSerial,
                            'other'
                        );
                        updatedItemObj.usedQtyExtPrice = getTotalExtendedPrice(
                            pricedItemsArr[pricedItemIdx].basePrice
                                ? pricedItemsArr[pricedItemIdx].basePrice
                                : '',
                            updatedItemObj.lotSerial,
                            'used'
                        );
                    }
                }
                if (existingItem.isLotSerialReqd) {
                    updatedItemObj.isLotSerialReqd =
                        existingItem.isLotSerialReqd;
                }
            } else if (
                existingItem.cfn?.toUpperCase().startsWith('SPS') ||
                existingItem.productCFN?.toUpperCase().startsWith('SPS')
            ) {
                updatedItemObj.isInvalidItemMsg = invalidSPSitemMsg();
            } else if (
                bundleHeadersArr &&
                bundleHeadersArr.includes(existingItem.cfn) &&
                existingItem.cfn.toUpperCase().startsWith('M1')
            ) {
                updatedItemObj.isInvalidItemMsg = invalidBundleItemMsg();
            } else if (!existingItem.accountOwned) {
                updatedItemObj.isInvalidItemMsg = invalidProductMsg();
            }
        }
        return updatedItemObj;
    });

    if (
        itemTableDataWithPrices?.length > 0 &&
        itemTableDataWithPrices.some(
            (item) => item.isSerialRequired && !item.isLot
        )
    ) {
        itemTableDataWithPrices.forEach((item, itemIdx) => {
            const itemLotSerialArr = isFromReportPaymentScreen
                ? itemTableDataWithPrices
                      .filter((rpItem) => rpItem.productCFN === item.productCFN)
                      .map((rpItem) => rpItem.lotSerialNumber)
                : flattenLotSerialArrayOfArrays(
                      'serialIssued',
                      itemTableDataWithPrices,
                      item.cfn
                  );
            if (itemLotSerialArr?.length > 0) {
                let areDuplicateSerialNumbersPresent = false,
                    firstSerialIdxInItemArr = -1;
                if (isFromReportPaymentScreen) {
                    areDuplicateSerialNumbersPresent =
                        itemLotSerialArr.filter(
                            (x) => x === item.lotSerialNumber
                        )?.length > 1;
                    firstSerialIdxInItemArr = itemTableDataWithPrices.findIndex(
                        (y) =>
                            y.productCFN === item.productCFN &&
                            y.lotSerialNumber === item.lotSerialNumber
                    );
                    itemTableDataWithPrices[itemIdx].isInvalidLotSerialMsg =
                        !itemTableDataWithPrices[itemIdx].isLot &&
                        itemTableDataWithPrices[itemIdx].isSerialRequired &&
                        areDuplicateSerialNumbersPresent &&
                        item.lotSerialNumber?.length > 0 &&
                        itemLotSerialArr.includes(item.lotSerialNumber) &&
                        firstSerialIdxInItemArr !== itemIdx
                            ? productIssueErrMsg
                            : itemTableDataWithPrices[itemIdx]
                                  .isInvalidLotSerialMsg;
                } else {
                    const serialNumArr = itemLotSerialArr.map((x) => x.Serial);
                    firstSerialIdxInItemArr = itemTableDataWithPrices.findIndex(
                        (data) =>
                            data.lotSerial.find(
                                (x) => x.id === getArrayItem(itemLotSerialArr)?.id
                            )
                    );
                    itemTableDataWithPrices[itemIdx].lotSerial =
                        item.lotSerial.map((serObj, serIdx) => {
                            areDuplicateSerialNumbersPresent =
                                serialNumArr.filter((x) => x === serObj.Serial)
                                    ?.length > 1;
                            const firstSerialIdxInSerialArr =
                                item.lotSerial.filter(
                                    (x) => x.Serial === serObj.Serial
                                )?.length > 1
                                    ? item.lotSerial.findIndex(
                                          (y) => y.Serial === serObj.Serial
                                      )
                                    : -1;
                            return {
                                ...serObj,
                                isInvalidLotSerialMsg:
                                    areDuplicateSerialNumbersPresent &&
                                    (itemIdx !== firstSerialIdxInItemArr ||
                                        (itemIdx === firstSerialIdxInItemArr &&
                                            firstSerialIdxInSerialArr >= 0 &&
                                            serIdx !==
                                                firstSerialIdxInSerialArr)) &&
                                    item.isSerialRequired &&
                                    !item.isLot &&
                                    serObj.Serial?.length > 0 &&
                                    serialNumArr.includes(serObj.Serial)
                                        ? productIssueErrMsg
                                        : serObj.isInvalidLotSerialMsg,
                            };
                        });
                }
            }
        });
    }

    return itemTableDataWithPrices;
};

export const getPriceInNumberDataType = (price) => {
    if (typeof price === 'number') {
        return parseFloat(price);
    }
    if (price) {
        const symbolRemovedPrice = price.replaceAll(/[^\d.,]/g, '');
        const seperators = price.match(/[.,]/g) || [];
        
        let decimalSeperator = '';
        let thousandSeperator = '';
        
        // Check if currency has no decimal places (e.g., JPY, KRW)
        if (numFormatObj.minimumFractionDigits === 0) {
            // For zero-decimal currencies, we need to distinguish between:
            // - Thousands separators: exactly 3 digits after (e.g., "122,845" or "1,234,567")
            // - Decimal separators: 1-2 digits after (e.g., "122845.00") - should be truncated
            if (seperators.length > 0) {
                const lastSeparator = seperators[seperators.length - 1];
                const lastSeparatorIndex = symbolRemovedPrice.lastIndexOf(lastSeparator);
                const digitsAfterLastSeparator = symbolRemovedPrice.substring(lastSeparatorIndex + 1);
                
                // If exactly 3 digits after last separator, it's a thousands separator
                // If 1-2 digits after, it's likely a decimal that should be truncated
                if (digitsAfterLastSeparator.length === 3) {
                    // This is a thousands separator (e.g., "122,845" -> 122845)
                    thousandSeperator = lastSeparator;
                } else if (digitsAfterLastSeparator.length >= 1 && digitsAfterLastSeparator.length <= 2) {
                    // This is a decimal separator (e.g., "122845.00" -> 122845)
                    decimalSeperator = lastSeparator;
                    thousandSeperator = seperators.length > 1 ? seperators[0] : '';
                } else {
                    // All separators are thousands separators
                    thousandSeperator = seperators[0];
                }
            }
        } else if (seperators.length > 0) {
            // Last separator is decimal separator
            decimalSeperator = seperators.pop() || '';
            thousandSeperator = seperators.length > 0 ? seperators[0] : '';
        }

        let numString =
            decimalSeperator !== ''
                ? symbolRemovedPrice
                      .replaceAll(thousandSeperator, '')
                      .replace(decimalSeperator, '.')
                : symbolRemovedPrice.replaceAll(thousandSeperator, '');
        
        // For zero-decimal currencies, truncate any decimal portion
        if (numFormatObj.minimumFractionDigits === 0 && numString.includes('.')) {
            numString = Math.floor(parseFloat(numString)).toString();
        }
        
        return parseFloat(numString);
    }
};

export const getValidateItemsArr = (productsList, country) => {
    const bundleItems = productsList.filter(
        (item) => item.isBundleItem && item.children && item.children.length > 0
    );
    const lotSerialUnknown = productsList.map((item) => item?.lotSerial?.some(i=>i?.lotSerialUnknown));
  
    const bundleItemChildren =
        bundleItems && bundleItems.length > 0
            ? bundleItems.reduce((prev, curr) => prev.concat(curr.children), [])
            : [];
    const bundleItemAccountOwned =
        bundleItemChildren && bundleItemChildren.length > 0
            ? bundleItemChildren.reduce(
                  (prev, curr) => prev.concat(curr.accountOwned),
                  []
              )
            : [];
    const bundleChildrenQty = bundleItemChildren.map(
        (child) =>
            child.lotSerial &&
            child.lotSerial.map(
                (i) =>
                    (i.Other || i.other ? Number(i.Other || i.other) : 0) +
                    (i.Used || i.used ? Number(i.Used || i.used) : 0)
            )
    );
    const bundleChildrenLotSerial = bundleItemChildren.map((child) =>
        child.lotSerial
            ? child.lotSerial.map((i) => (i.Serial ? i.Serial : ' '))
            : []
    );
    const arrProductCFN = productsList
        .map((item) => item.cfn)
        .concat(bundleItemChildren.map((x) => x.cfn));
    const arrLotSerialNumber = productsList
        .map((item) =>
            item.lotSerial
                ? item.lotSerial.map((i) => (i?.lotSerialUnknown?'unknown':i?.Serial ? i?.Serial : ' '))
                : []
        )
        .concat(bundleChildrenLotSerial);
    const arrAccountOwned = productsList
        .map((item) => (item.accountOwned ? true : false))
        .concat(bundleItemAccountOwned);
    const arrQuantity = productsList
        .map(
            (item) =>
                item.lotSerial &&
                item.lotSerial.map(
                    (i) =>
                        (i.Other || i.other ? Number(i.Other || i.other) : 0) +
                        (i.Used || i.used ? Number(i.Used || i.used) : 0)
                )
        )
        .concat(bundleChildrenQty);

    return arrProductCFN.map((cfn, i) => ({
        lotSerialNumber: arrLotSerialNumber[i].toString(),
        lotSerialUnknown: lotSerialUnknown[i]?.toString(),
        productCFN: cfn,
        accountOwned: arrAccountOwned[i],
        quantity: arrQuantity[i][0].toString(),
    }));
};

export const getRPValidateItemsArr = (productsList) => {
    if (!productsList || !Array.isArray(productsList) || productsList.length === 0) {
        return [];
    }
    
    const result = [];
    
    // Process main items - flatten lotSerial array
    productsList.forEach((item) => {
        const cfn = item.cfn || item.productCfn;
        
        if (item.lotSerial && item.lotSerial.length > 0) {
            // Flatten: create one entry per lotSerial item
            item.lotSerial.forEach((ls) => {
                const usedVal = ls.used !== undefined ? ls.used : ls.usedQuantity;
                const otherVal = ls.other !== undefined ? ls.other : ls.otherQuantity;
                const usedQty = usedVal === true ? 1 : (parseInt(usedVal) || 0);
                const otherQty = otherVal === true ? 1 : (parseInt(otherVal) || 0);
                const quantity = usedQty + otherQty;
                
                result.push({
                    lotSerialNumber: ls?.lotSerialUnknown
                        ? 'unknown'
                        : ls?.lotSerialNumber ?? ls?.Serial ?? '',
                    lotSerialUnknown: ls?.lotSerialUnknown?.toString(),
                    productCFN: cfn,
                    quantity: quantity.toString(),
                });
            });
        } else {
            // Fallback for flat structure (lotSerialNumber at item level)
            const usedQty = item.usedQuantity ? Number(item.usedQuantity) : 0;
            const otherQty = item.otherQuantity ? Number(item.otherQuantity) : 0;
            const quantity = usedQty + otherQty || 1;
            
            result.push({
                lotSerialNumber: item?.lotSerialUnknown ? 'unknown' : (item.lotSerialNumber || ''),
                lotSerialUnknown: item?.lotSerialUnknown?.toString(),
                productCFN: cfn,
                quantity: quantity.toString(),
            });
        }
    });
    
    // Process bundle children (original approach)
    const bundleItemChildren =
        productsList.filter((item) => item.bundleItem?.length > 0)?.length > 0
            ? productsList
                  .filter((item) => item.bundleItem?.length > 0)
                  .reduce((prev, curr) => prev.concat(curr.bundleItem), [])
            : [];
    const bundleChildrenQty = bundleItemChildren.map((child) =>
        child.quantity || child.used ? Number(child.quantity || child.used) : 0
    );
    // code  need to look for bundle 
    const bundleCfnArr = bundleItemChildren.map((x) => x.cfn || x.productCFN);
    const bundleLotSerialArr = bundleItemChildren.map((child) =>
        child?.lotSerialUnknown ? 'unknown' : child.lotSerialNumber ? child.lotSerialNumber : ''
    );
    const bundleLotSerialUnknown = bundleItemChildren.map((child) => child?.lotSerialUnknown);
    
    bundleCfnArr.forEach((cfn, i) => {
        result.push({
            lotSerialNumber: bundleLotSerialArr[i]?.toString() || '',
            lotSerialUnknown: bundleLotSerialUnknown[i]?.toString(),
            productCFN: cfn,
            quantity: bundleChildrenQty[i].toString(),
        });
    });
      // code  need to look for bundle 
    return result;
};

export const getItemTableDataWithQtyValidation = (
    itemTableData,
    myCaseResourceType
) => {
    if (myCaseResourceType === 'reportPayment') {
        return itemTableData.map((item) => {
            let updatedChildrenArr;
            if (
                item.isBundleItem &&
                item.bundleItem &&
                item.bundleItem.length > 0
            ) {
                updatedChildrenArr = item.bundleItem.map((child) => {
                    const qty = { used: child.quantity || child.usedQuantity };
                    return {
                        ...child,
                        isInvalidQtyMsg: !checkIfValidQuantities(qty)
                            ? t(
                                  'productValidationMessages.mustEnterUsedQuantity'
                              )
                            : null,
                    };
                });
            }
            // Check quantity from item level or lotSerial array (supports both used/other and usedQuantity/otherQuantity)
            const hasLotSerialQty = item.lotSerial && item.lotSerial.length > 0 &&
                item.lotSerial.some((ls) => {
                    const usedVal = ls.used !== undefined ? ls.used : ls.usedQuantity;
                    const otherVal = ls.other !== undefined ? ls.other : ls.otherQuantity;
                    return (usedVal && (parseInt(usedVal) > 0 || usedVal === true)) ||
                           (otherVal && (parseInt(otherVal) > 0 || otherVal === true));
                });
            // Get quantity from lotSerial array if item-level quantities are undefined
            const usedQtyFromLotSerial = item.lotSerial && item.lotSerial.length > 0
                ? item.lotSerial.reduce((sum, ls) => {
                    const usedVal = ls.used !== undefined ? ls.used : ls.usedQuantity;
                    const val = usedVal === true ? 1 : (parseInt(usedVal) || 0);
                    return sum + val;
                  }, 0)
                : undefined;
            const otherQtyFromLotSerial = item.lotSerial && item.lotSerial.length > 0
                ? item.lotSerial.reduce((sum, ls) => {
                    const otherVal = ls.other !== undefined ? ls.other : ls.otherQuantity;
                    const val = otherVal === true ? 1 : (parseInt(otherVal) || 0);
                    return sum + val;
                  }, 0)
                : undefined;
            const qty = { 
                used: item.usedQuantity !== undefined ? item.usedQuantity : usedQtyFromLotSerial, 
                other: item.otherQuantity !== undefined ? item.otherQuantity : otherQtyFromLotSerial 
            };
            const isInvalidQtyMsg =
                !item.isInvalidItemMsg && !checkIfValidQuantities(qty) && !hasLotSerialQty
                    ? item.isBundleItem
                        ? t('productValidationMessages.mustEnterUsedQuantity')
                        : item.salesOrg === 'S030'
                        ? t('productValidationMessages.mustSelectUsedQuantity')
                        : t('productValidationMessages.mustEnterUsedQuantity')
                    : item?.cfn?.startsWith('S33') &&
                      (parseInt(qty.used) > 1 || parseInt(qty.other) > 1)
                    ? t('productValidationMessages.feeQtyCanOnlyOne')
                    : null;
            const itemObj = {
                ...item,
                isInvalidQtyMsg: isInvalidQtyMsg,
            };
            if (item.isBundleItem) {
                if (updatedChildrenArr && updatedChildrenArr.length > 0)
                    itemObj.bundleItem = updatedChildrenArr;
                else itemObj.noItemInBundleErr = emptyBundleMsg();
            }
            return itemObj;
        });
    } else {
        return itemTableData.map((item) => {
            let updatedChildrenArr;
            if (
                item.isBundleItem &&
                item.children &&
                item.children.length > 0
            ) {
                updatedChildrenArr = item.children.map((child) => {
                    const qty = { used: getArrayItem(child.lotSerial).used };
                    const childWithFeeItem =
                        child?.cfn?.startsWith('S33') &&
                        (parseInt(qty.used) > 1 || parseInt(qty.other) > 1);

                    const updatedChildLotSerialArr = child.lotSerial.map(
                        (x) => ({
                            ...x,
                            isInvalidQtyMsg: !checkIfValidQuantities(x)
                                ? t(
                                      'productValidationMessages.mustEnterUsedQuantity'
                                  )
                                : null || childWithFeeItem
                                ? t(
                                      'productValidationMessages.feeQtyCanOnlyOne'
                                  )
                                : null,
                        })
                    );
                    return {
                        ...child,
                        lotSerial: updatedChildLotSerialArr,
                    };
                });
            }
            const updatedLotSerialArr = item?.lotSerial?.map((x) => ({
                ...x,
                isInvalidQtyMsg:
                    !item.isInvalidItemMsg && !checkIfValidQuantities(x)
                        ? item.isBundleItem
                            ? t(
                                  'productValidationMessages.mustEnterUsedQuantity'
                              )
                            : item.salesOrg === 'S030'
                            ? t(
                                  'productValidationMessages.mustSelectUsedOrOtherQuantity'
                              )
                            : t(
                                  'productValidationMessages.mustEnterUsedOrOtherQuantity'
                              )
                        : item?.cfn?.startsWith('S33') &&
                          getTotalQuantity(item.lotSerial) > 1
                        ? t('productValidationMessages.feeQtyCanOnlyOne')
                        : null,
            }));

            const itemObj = {
                ...item,
                lotSerial: updatedLotSerialArr,
            };
            if (item.isBundleItem) {
                if (updatedChildrenArr && updatedChildrenArr.length > 0)
                    itemObj.children = updatedChildrenArr;
                else itemObj.noItemInBundleErr = emptyBundleMsg();
            }
            return itemObj;
        });
    }
};

export const getValidatedBundleChildren = (
    existingItems,
    existingChildren,
    validatedItems
) => {
    const validItemsCFN = validatedItems.map((x) => x.productCFN);

    const bundlesInOrder = existingItems.filter(
        (item) =>
            item.isBundleItem &&
            ((item.children && item.children.length > 0) ||
                (item.bundleItem && item.bundleItem.length > 0))
    );
    const currentBundleIdx = bundlesInOrder.findIndex(
        (b) =>
            b.children === existingChildren ||
            b.bundleItem === existingChildren
    );
    const safeBundleIdx = currentBundleIdx === -1 ? 0 : currentBundleIdx;
    const childrenStartIdx =
        validatedItems.length -
        bundlesInOrder.reduce(
            (acc, b) => acc + (b.children?.length || b.bundleItem?.length || 0),
            0
        ) +
        bundlesInOrder
            .slice(0, safeBundleIdx)
            .reduce(
                (acc, b) =>
                    acc + (b.children?.length || b.bundleItem?.length || 0),
                0
            );

    return existingChildren.map((child, childIdx) => {
        const notBundleItemsIdxArr = existingItems.map((o, i) =>
            !o.isBundleItem ? i : -1
        );
        const childCFN = child.productCFN ?? child.cfn;
        const isMultipleSameChildItem =
            validatedItems.filter(
                (vc, i) =>
                    childCFN === vc.productCFN &&
                    !notBundleItemsIdxArr.includes(i)
            )?.length > 1;
        const validatedChildren = validatedItems.filter(
            (vItem, vIdx) =>
                vIdx >= childrenStartIdx &&
                vIdx < childrenStartIdx + existingChildren.length &&
                existingChildren
                    .map((y) => y.productCFN ?? y.cfn)
                    ?.includes(vItem.productCFN)
        );
        const validatedProduct =
            isMultipleSameChildItem || validatedChildren?.length > 0
                ? validatedChildren[childIdx] ?? getArrayItem(validatedChildren)
                : validatedItems.filter(
                      (val) => childCFN === val.productCFN
                  ) &&
                  getArrayItem(validatedItems.filter(
                      (val) => childCFN === val.productCFN
                  ));

        const hasLotSerialData =
            validatedProduct &&
            Object.prototype.hasOwnProperty.call(
                validatedProduct,
                'lotSerialData'
            );
        const isLotSerialReqd = Object.prototype.hasOwnProperty.call(
            child,
            'isLotSerialReqd'
        )
            ? child.isLotSerialReqd
            : Object.prototype.hasOwnProperty.call(child, 'isSerialRequired')
            ? child.isSerialRequired
            : hasLotSerialData;

        const isUNiDPlanFeeItemArr = validatedItems.filter(
            (val) => val.productCFN === childCFN
        );

        const isUNiDPlanFeeItem = getArrayItem(isUNiDPlanFeeItemArr)?.isUNiDPlanFeeItem;
        const isReplenishmentRequired =
            validatedProduct?.replenishmentrequired !== false;

        const validatedLotSerialArr = hasLotSerialData
            ? getLotSerialValidationData(child, validatedProduct)
            : [];

        const updatedLotSerialArr = hasLotSerialData
            ? child?.lotSerial?.map((x) => {
                  if (
                      validatedLotSerialArr.find(
                          (vLot) =>
                              x.Serial &&
                              x.Serial.length > 0 &&
                              (vLot.lotSerialNumber === x.Serial|| vLot.lotSerialNumber ==='unknown') &&
                              (vLot.statusCode === '10712' ||
                                  vLot.statusCode === '11902' ||
                                  vLot.statusCode === '11903' ||
                                  vLot.statusCode === '11904' ||
                                  vLot.statusCode === '11907' ||
                                  vLot.statusCode === '11905' ||
                                  vLot.statusCode === '11906' ||
                                  vLot.statusCode === '10826' ||
                                  vLot.statusCode === '11909')
                      )
                  ) {
                      if (
                          validatedLotSerialArr.find((vLot) => {
                              return vLot.statusCode === '10826';
                          })
                      ) {
                          const feeItemQtyError = validatedLotSerialArr.find(
                              (vLot) => {
                                  return vLot.statusCode === '10826';
                              }
                          );
                          return {
                              ...x,
                              isInvalidQtyMsg: feeItemQtyError?.message
                                  ? feeItemQtyError.message
                                  : null,
                          };
                      } else {
                          const isVendorCaseId = validatedLotSerialArr.find(
                              (vLot) => {
                                  return (
                                      vLot.statusCode === '11902' ||
                                      vLot.statusCode === '11903' ||
                                      vLot.statusCode === '11904' ||
                                      vLot.statusCode === '11907' ||
                                      vLot.statusCode === '11905' ||
                                      vLot.statusCode === '11906' ||
                                      vLot.statusCode === '11909'
                                  );
                              }
                          );
                          return {
                              ...x,
                              isInvalidLotSerialMsg: isVendorCaseId?.message
                                  ? isVendorCaseId.message
                                  : (child.isSerialRequired && !child.isLot) ||
                                    child.salesOrg === 'S030'
                                  ? invalidSerialNumberMsg()
                                  : invalidLotNumberMsg(),
                          };
                      }
                  } else if (
                      validatedLotSerialArr.find(
                          (vLot) =>
                              (!x.Serial ||
                                  (x.Serial && x.Serial.length === 0)) &&
                              (vLot.statusCode === '10712' ||
                                  vLot.statusCode === '11902' ||
                                  vLot.statusCode === '11903' ||
                                  vLot.statusCode === '11904' ||
                                  vLot.statusCode === '11907' ||
                                  vLot.statusCode === '11905' ||
                                  vLot.statusCode === '11906' ||
                                  vLot.statusCode === '10826' ||
                                  vLot.statusCode === '11909')
                      )
                  ) {
                      if (
                          validatedLotSerialArr.find((vLot) => {
                              return vLot.statusCode === '10826';
                          })
                      ) {
                          const feeItemQtyError = validatedLotSerialArr.find(
                              (vLot) => {
                                  return vLot.statusCode === '10826';
                              }
                          );
                          return {
                              ...x,
                              isInvalidQtyMsg: feeItemQtyError?.message
                                  ? feeItemQtyError.message
                                  : null,
                          };
                      } else if (isLotSerialReqd) {
                          const isVendorCaseId = validatedLotSerialArr.find(
                              (vLot) => {
                                  return (
                                      vLot.statusCode === '11902' ||
                                      vLot.statusCode === '11903' ||
                                      vLot.statusCode === '11904' ||
                                      vLot.statusCode === '11907' ||
                                      vLot.statusCode === '11905' ||
                                      vLot.statusCode === '11906' ||
                                      vLot.statusCode === '11909'
                                  );
                              }
                          );
                          return {
                              ...x,
                              isInvalidLotSerialMsg: isVendorCaseId?.message
                                  ? isVendorCaseId.message
                                  : (child.isSerialRequired && !child.isLot) ||
                                    child.salesOrg === 'S030'
                                  ? serialNumberReqdMsg()
                                  : lotNumberReqdMsg(),
                          };
                      } else {
                          return { ...x, isInvalidLotSerialMsg: null };
                      }
                  } else {
                      return { ...x, isInvalidLotSerialMsg: null };
                  }
              })
            : child?.lotSerial?.map((x) => ({
                  ...x,
                  isInvalidLotSerialMsg: null,
              }));

        return {
            ...child,
            isInvalidItemMsg: !validItemsCFN.includes(childCFN)
                ? invalidProductMsg()
                : null,
            isLotSerialReqd: isLotSerialReqd,
            lotSerial: updatedLotSerialArr,
            isUNiDPlanFeeItem: isUNiDPlanFeeItem,
            isReplenishmentRequired: isReplenishmentRequired,
        };
    });
};

export const getValidatedItemTableData = (
    existingItems,
    validatedItems,
    myCaseResourceType
) => {
    const existingItemsWithQtyValidation =
        existingItems?.length > 0 &&
        getItemTableDataWithQtyValidation(existingItems, myCaseResourceType);

    const validItemsCFN = validatedItems?.map((x) => x.productCFN);

    return (
        existingItemsWithQtyValidation?.length > 0
            ? existingItemsWithQtyValidation
            : existingItems
    )?.map((obj, itemIdx) => {
        const isMultipleSameItem =
            validatedItems.filter((v) => obj.cfn === v.productCFN)?.length > 1;

        const validatedProduct =
            validatedItems.filter(
                (val, vIdx) =>
                    obj.cfn === val.productCFN &&
                    (isMultipleSameItem ? itemIdx === vIdx : true)
            ) &&
            getArrayItem(validatedItems.filter(
                (val, vIdx) =>
                    obj.cfn === val.productCFN &&
                    (isMultipleSameItem ? itemIdx === vIdx : true)
            ));

        const isInvalidUnidItemQuantity =
            validatedProduct?.isUnidRodItem &&
            validatedProduct?.statusMessage ===
                t('productValidationMessages.enteredQualityExceeds');

        const hasLotSerialData =
            validatedProduct &&
            Object.prototype.hasOwnProperty.call(
                validatedProduct,
                'lotSerialData'
            );
        const isLotSerialReqd = Object.prototype.hasOwnProperty.call(
            obj,
            'isLotSerialReqd'
        )
            ? obj.isLotSerialReqd
            : Object.prototype.hasOwnProperty.call(obj, 'isSerialRequired')
            ? obj.isSerialRequired
            : hasLotSerialData;
        const isUNiDPlanFeeItem =
            validatedProduct && validatedProduct.isUNiDPlanFeeItem;
        const isReplenishmentRequired =
            validatedProduct?.replenishmentrequired !== false;
        const validatedLotSerialArr =
            hasLotSerialData && validatedProduct
                ? getLotSerialValidationData(obj, validatedProduct)
                : [];

        const updatedLotSerialArr = hasLotSerialData
            ? obj?.lotSerial?.map((x) => {
                  if (
                      validatedLotSerialArr.find(
                          (vLot) =>
                              x.Serial &&
                              x.Serial.length > 0 &&
                              (vLot.lotSerialNumber!=='unknown'&&vLot.lotSerialNumber===x.Serial) &&
                              (vLot.statusCode === '10712' ||
                                  vLot.statusCode === '11902' ||
                                  vLot.statusCode === '11903' ||
                                  vLot.statusCode === '11904' ||
                                  vLot.statusCode === '11907' ||
                                  vLot.statusCode === '11905' ||
                                  vLot.statusCode === '11906' ||
                                  vLot.statusCode === '10826' ||
                                  vLot.statusCode === '11909')
                      )
                  ) {
                      if (
                          validatedLotSerialArr.find((vLot) => {
                              return vLot.statusCode === '10826';
                          })
                      ) {
                          const feeItemQtyError = validatedLotSerialArr.find(
                              (vLot) => {
                                  return vLot.statusCode === '10826';
                              }
                          );
                          return {
                              ...x,
                              isInvalidQtyMsg: feeItemQtyError?.message
                                  ? feeItemQtyError.message
                                  : null,
                          };
                      } else {
                          const isVendorCaseId = validatedLotSerialArr.find(
                              (vLot) => {
                                  return (
                                      vLot.statusCode === '11902' ||
                                      vLot.statusCode === '11903' ||
                                      vLot.statusCode === '11904' ||
                                      vLot.statusCode === '11907' ||
                                      vLot.statusCode === '11905' ||
                                      vLot.statusCode === '11906' ||
                                      vLot.statusCode === '11909'
                                  );
                              }
                          );
                          return {
                              ...x,
                              isInvalidLotSerialMsg: isVendorCaseId?.message
                                  ? isVendorCaseId.message
                                  : (obj.isSerialRequired && !obj.isLot) ||
                                    obj.salesOrg === 'S030'
                                  ? invalidSerialNumberMsg()
                                  : invalidLotNumberMsg(),
                              linkedSstref:
                                  isVendorCaseId?.linkedSstref &&
                                  isVendorCaseId?.linkedSstref,
                          };
                      }
                  } else if (
                      validatedLotSerialArr.find(
                          (vLot) =>
                              (!x.Serial ||
                                  (x.Serial && x.Serial.length === 0)) &&!x.lotSerialUnknown&&
                              (vLot.statusCode === '10712' ||
                                  vLot.statusCode === '11902' ||
                                  vLot.statusCode === '11903' ||
                                  vLot.statusCode === '11904' ||
                                  vLot.statusCode === '11907' ||
                                  vLot.statusCode === '11905' ||
                                  vLot.statusCode === '11906' ||
                                  vLot.statusCode === '10826' ||
                                  vLot.statusCode === '11909')
                      )
                  ) {
                      if (
                          validatedLotSerialArr.find((vLot) => {
                              return vLot.statusCode === '10826';
                          })
                      ) {
                          const feeItemQtyError = validatedLotSerialArr.find(
                              (vLot) => {
                                  return vLot.statusCode === '10826';
                              }
                          );
                          return {
                              ...x,
                              isInvalidQtyMsg: feeItemQtyError?.message
                                  ? feeItemQtyError.message
                                  : null,
                          };
                      } else if (isLotSerialReqd) {
                          const isVendorCaseId = validatedLotSerialArr.find(
                              (vLot) => {
                                  return (
                                      vLot.statusCode === '11902' ||
                                      vLot.statusCode === '11903' ||
                                      vLot.statusCode === '11904' ||
                                      vLot.statusCode === '11907' ||
                                      vLot.statusCode === '11905' ||
                                      vLot.statusCode === '11906' ||
                                      vLot.statusCode === '11909'
                                  );
                              }
                          );
                          return {
                              ...x,
                              isInvalidLotSerialMsg: isVendorCaseId?.message
                                  ? isVendorCaseId.message
                                  : (obj.isSerialRequired && !obj.isLot) ||
                                    obj.salesOrg === 'S030'
                                  ? serialNumberReqdMsg()
                                  : lotNumberReqdMsg(),
                          };
                      } else {
                          return { ...x, isInvalidLotSerialMsg: null };
                      }
                  } else {
                      return { ...x, isInvalidLotSerialMsg: null };
                  }
              })
            : obj?.lotSerial?.map((x) => {
                  //   let totalUnidQty, unidDeliveredQty;
                  //   if (obj.isUnidRodItem && obj.batchQtyObjArr?.length > 0) {
                  //       const batchQtyObj = obj.batchQtyObjArr.filter(
                  //           (b) => b.batch === x.Serial && b.deliveredQty
                  //       );
                  //       if (batchQtyObj && batchQtyObj[0]) {
                  //           unidDeliveredQty = batchQtyObj[0].deliveredQty;
                  //       }
                  //       const usedQty =
                  //           isNaN(x.used) || !x.used ? 0 : parseInt(x.used);
                  //       const otherQty =
                  //           isNaN(x.other) || !x.other ? 0 : parseInt(x.other);
                  //       totalUnidQty = usedQty + otherQty;
                  //   }
                  return {
                      ...x,
                      selectedBatchValue: isInvalidUnidItemQuantity
                          ? x.Serial
                          : null,
                      isInvalidQtyMsg:
                          //   totalUnidQty &&
                          //   unidDeliveredQty &&
                          //   totalUnidQty > parseInt(unidDeliveredQty)
                          isInvalidUnidItemQuantity
                              ? invalidUnidRodItemQtyMsg
                              : x.isInvalidQtyMsg,
                      isInvalidLotSerialMsg: null,
                  };
              });

        const validatedItemObj = {
            ...obj,
            isInvalidItemMsg: !validItemsCFN.includes(obj.cfn)
                ? invalidProductMsg()
                : null,
            isLotSerialReqd: isLotSerialReqd,
            lotSerial: updatedLotSerialArr,
            isInvalidUnidItemQuantity,
            isUNiDPlanFeeItem: isUNiDPlanFeeItem,
            isReplenishmentRequired: isReplenishmentRequired,
        };
        if (validatedProduct && validatedProduct.isUnidRodItem) {
            validatedItemObj.isUnidRodItem = validatedProduct.isUnidRodItem;
        }
        if (obj.isBundleItem && obj.children && obj.children.length > 0) {
            const updatedChildrenArr = getValidatedBundleChildren(
                existingItemsWithQtyValidation?.length > 0
                    ? existingItemsWithQtyValidation
                    : existingItems,
                obj.children,
                validatedItems
            );
            validatedItemObj.children = updatedChildrenArr;
        }
        if (
            myCaseResourceType === 'reportPayment' &&
            obj.isBundleItem &&
            obj.bundleItem &&
            obj.bundleItem.length > 0
        ) {
            const updatedChildrenArr = getValidatedBundleChildren(
                existingItemsWithQtyValidation?.length > 0
                    ? existingItemsWithQtyValidation
                    : existingItems,
                obj.bundleItem,
                validatedItems
            );
            validatedItemObj.bundleItem = updatedChildrenArr;
        }
        return validatedItemObj;
    });
};

export const getRPValidatedChildrenData = (
    existingItems,
    existingChildren,
    parentIdx,
    validatedItems,
    countryISOCode
) => {
    const validItemsCFN = validatedItems.map((x) => x.productCFN || x.cfn);
    const rpBundleItems = existingItems.filter(
        (item) =>
            item.isBundleItem && item.bundleItem && item.bundleItem.length > 0
    );
    const bundleChildren =
        rpBundleItems && rpBundleItems.length > 0
            ? rpBundleItems
                  .reduce((prev, curr) => prev.concat(curr.bundleItem), [])
                  .filter(
                      (x) =>
                          (countryISOCode === 'US'
                              ? x.salesOrg === 'S002'
                              : x.salesOrg !== 'S030') &&
                          x.materialType !== 'DIEN'
                  )
            : [];
    return existingChildren.map((obj, itemIdx) => {
        if (obj.isAdditionalCartChild) {
            const notBundleItemsIdxArr = existingItems.map((o, i) =>
                !o.isBundleItem ? i : -1
            );
            const isMultipleSameItem =
                validatedItems.filter(
                    (vc, i) =>
                        obj.productCFN === vc.productCFN &&
                        !notBundleItemsIdxArr.includes(i)
                )?.length > 1;
            // const isMultipleSameItem =
            //     validatedItems.filter((v) => obj.cfn === v.productCFN)?.length > 1;

            // const validatedProduct =
            //     validatedItems.filter(
            //         (val, vIdx) =>
            //             obj.cfn === val.productCFN &&
            //             (isMultipleSameItem ? itemIdx === vIdx : true)
            //     ) &&
            //     validatedItems.filter(
            //         (val, vIdx) =>
            //             obj.cfn === val.productCFN &&
            //             (isMultipleSameItem ? itemIdx === vIdx : true)
            //     )[0];
            const validatedChildren = validatedItems.filter(
                (vItem, vIdx) =>
                    !notBundleItemsIdxArr.includes(vIdx) &&
                    existingChildren
                        .map((y) => y.productCFN)
                        ?.includes(vItem.productCFN)
            );
            const validatedProduct =
                isMultipleSameItem || validatedChildren?.length > 1
                    ? validatedChildren[itemIdx]
                    : validatedItems.filter(
                          (val) => obj.productCFN === val.productCFN
                      ) &&
                      getArrayItem(validatedItems.filter(
                          (val) => obj.productCFN === val.productCFN
                      ));
            const isUNiDPlanFeeItem =
                validatedProduct && validatedProduct.isUNiDPlanFeeItem;
            const isReplenishmentRequired =
                validatedProduct?.replenishmentrequired !== false;
            const isInvalidUnidItemQuantity =
                validatedProduct?.isUnidRodItem &&
                validatedProduct?.statusMessage ===
                    t('productValidationMessages.enteredQualityExceeds');

            const isLotSerialReqd =
                validatedProduct &&
                Object.prototype.hasOwnProperty.call(
                    validatedProduct,
                    'lotSerialData'
                );

            const validatedLotSerialArr =
                isLotSerialReqd && validatedProduct
                    ? getLotSerialValidationData(obj, validatedProduct)
                    : [];
            let isInvalidFeeQty = null,
                linkedSstref = null,
                isInvalidLotSerialMsg = null;
            if (isLotSerialReqd) {
                // Get serial number from either item level or lotSerial array (for tissue items)
                const itemSerialNumber = obj.lotSerialNumber || 
                    (obj?.lotSerial && obj?.lotSerial?.length > 0 ? (getArrayItem(obj?.lotSerial)?.Serial || getArrayItem(obj?.lotSerial)?.lotSerialNumber) : null);
                if (
                    validatedLotSerialArr.find(
                        (vLot) =>
                            itemSerialNumber &&
                            itemSerialNumber.length > 0 &&
                            (vLot.lotSerialNumber==='unknown' || vLot.lotSerialNumber===itemSerialNumber )&&
                            (vLot.statusCode === '10712' ||
                                vLot.statusCode === '11902' ||
                                vLot.statusCode === '11903' ||
                                vLot.statusCode === '11904' ||
                                vLot.statusCode === '11907' ||
                                vLot.statusCode === '11905' ||
                                vLot.statusCode === '11906' ||
                                vLot.statusCode === '10826' ||
                                vLot.statusCode === '11909')
                    )
                ) {
                    const feeItemQtyError = validatedLotSerialArr.find(
                        (vLot) => {
                            return vLot.statusCode === '10826';
                        }
                    );
                    if (feeItemQtyError) {
                        isInvalidFeeQty = feeItemQtyError?.message
                            ? feeItemQtyError.message
                            : null;
                    } else {
                        const isVendorCaseId = validatedLotSerialArr.find(
                            (vLot) => {
                                return (
                                    vLot.statusCode === '11902' ||
                                    vLot.statusCode === '11903' ||
                                    vLot.statusCode === '11904' ||
                                    vLot.statusCode === '11907' ||
                                    vLot.statusCode === '11905' ||
                                    vLot.statusCode === '11906' ||
                                    vLot.statusCode === '11909'
                                );
                            }
                        );
                        isInvalidLotSerialMsg = isVendorCaseId?.message
                            ? isVendorCaseId.message
                            : (obj.isSerialRequired && !obj.isLot) ||
                              obj.salesOrg === 'S030'
                            ? invalidSerialNumberMsg()
                            : invalidLotNumberMsg();
                        linkedSstref =
                            isVendorCaseId?.linkedSstref &&
                            isVendorCaseId.linkedSstref;
                    }
                } else if (
                    validatedLotSerialArr.find(
                        (vLot) => {
                            // Check if any lotSerial item has a valid Serial value
                            const hasSerialFromArray = obj.lotSerial?.every(ls => ls?.Serial && ls.Serial.length > 0);
                            const serialValue = obj.lotSerialNumber || (hasSerialFromArray ? 'hasValue' : '');
                            // Check if lotSerialUnknown is set at item level or in any lotSerial entry
                            const isUnknown = obj.lotSerialUnknown || obj.lotSerial?.some(ls => ls?.lotSerialUnknown);
                            return (!serialValue || serialValue.length === 0) &&
                            !isUnknown &&
                            (vLot.statusCode === '10712' ||
                                vLot.statusCode === '11902' ||
                                vLot.statusCode === '11903' ||
                                vLot.statusCode === '11904' ||
                                vLot.statusCode === '11907' ||
                                vLot.statusCode === '11905' ||
                                vLot.statusCode === '11906' ||
                                vLot.statusCode === '10826' ||
                                vLot.statusCode === '11909');
                        }
                    )
                ) {
                    const feeItemQtyError = validatedLotSerialArr.find(
                        (vLot) => {
                            return vLot.statusCode === '10826';
                        }
                    );
                    if (feeItemQtyError) {
                        isInvalidFeeQty = feeItemQtyError?.message
                            ? feeItemQtyError.message
                            : null;
                    } else {
                        const isVendorCaseId = validatedLotSerialArr.find(
                            (vLot) => {
                                return (
                                    vLot.statusCode === '11902' ||
                                    vLot.statusCode === '11903' ||
                                    vLot.statusCode === '11904' ||
                                    vLot.statusCode === '11907' ||
                                    vLot.statusCode === '11905' ||
                                    vLot.statusCode === '11906' ||
                                    vLot.statusCode === '11909'
                                );
                            }
                        );
                        isInvalidLotSerialMsg = isVendorCaseId?.message
                            ? isVendorCaseId.message
                            : (obj.isSerialRequired && !obj.isLot) ||
                              obj.salesOrg === 'S030'
                            ? serialNumberReqdMsg()
                            : lotNumberReqdMsg();
                        linkedSstref =
                            isVendorCaseId?.linkedSstref &&
                            isVendorCaseId.linkedSstref;
                    }
                } else {
                    isInvalidLotSerialMsg = null;
                }
            } else {
                isInvalidLotSerialMsg = null;
            }

            return {
                ...obj,
                isInvalidItemMsg: !validItemsCFN.includes(
                    obj.cfn || obj.productCFN
                )
                    ? invalidProductMsg()
                    : null,
                isLotSerialReqd: isLotSerialReqd,
                isInvalidLotSerialMsg: isInvalidLotSerialMsg,
                linkedSstref: linkedSstref,
                isInvalidQtyMsg: isInvalidFeeQty
                    ? isInvalidFeeQty
                    : isInvalidUnidItemQuantity
                    ? invalidUnidRodItemQtyMsg
                    : obj.isInvalidQtyMsg,
                isInvalidUnidItemQuantity,
                selectedBatchValue: isInvalidUnidItemQuantity
                    ? obj.lotSerialNumber
                    : null,
                isUNiDPlanFeeItem: isUNiDPlanFeeItem,
                isReplenishmentRequired: isReplenishmentRequired,
            };
        } else {
            const notBundleItemsIdxArr = existingItems.map((o, i) =>
                !o.isBundleItem ? i : -1
            );
            const isMultipleSameItem =
                validatedItems.filter(
                    (vc, i) =>
                        obj.productCFN === vc.productCFN &&
                        !notBundleItemsIdxArr.includes(i)
                )?.length > 1;
            const validatedChildren = validatedItems.filter(
                (vItem, vIdx) =>
                    !notBundleItemsIdxArr.includes(vIdx) &&
                    existingChildren
                        .map((y) => y.productCFN)
                        ?.includes(vItem.productCFN)
            );
            const validatedProduct =
                isMultipleSameItem || validatedChildren?.length > 1
                    ? validatedChildren[itemIdx]
                    : validatedItems.filter(
                          (val) => obj.productCFN === val.productCFN
                      ) &&
                      getArrayItem(validatedItems.filter(
                          (val) => obj.productCFN === val.productCFN
                      ));
            const isUNiDPlanFeeItem =
                validatedProduct && validatedProduct.isUNiDPlanFeeItem;
            const isReplenishmentRequired =
                validatedProduct?.replenishmentrequired !== false;
            return {
                ...obj,
                isUNiDPlanFeeItem: isUNiDPlanFeeItem,
                isReplenishmentRequired: isReplenishmentRequired,
            };
        }
    });
};

export const getRPValidatedItemTableData = (
    existingItems,
    validatedItems,
    countryISOCode
) => {
    const validItemsCFN = validatedItems.map((x) => x.productCFN || x.cfn);
    return existingItems.map((obj, itemIdx) => {
        // if (!obj.isAdditionalCartEntry) return obj;

        const isMultipleSameItem =
            validatedItems.filter((v) => obj.cfn === v.productCFN)?.length > 1;

        const validatedProduct =
            validatedItems.filter(
                (val, vIdx) =>
                    obj.cfn === val.productCFN &&
                    (isMultipleSameItem ? itemIdx === vIdx : true)
            ) &&
            getArrayItem(validatedItems.filter(
                (val, vIdx) =>
                    obj.cfn === val.productCFN &&
                    (isMultipleSameItem ? itemIdx === vIdx : true)
            ));
        const isUNiDPlanFeeItem =
            validatedProduct && validatedProduct.isUNiDPlanFeeItem;
        const isReplenishmentRequired =
            validatedProduct?.replenishmentrequired !== false;
        const isInvalidUnidItemQuantity =
            validatedProduct?.isUnidRodItem &&
            validatedProduct?.statusMessage ===
                t('productValidationMessages.enteredQualityExceeds');

        const hasLotSerialData =
            validatedProduct &&
            Object.prototype.hasOwnProperty.call(
                validatedProduct,
                'lotSerialData'
            );
        const isLotSerialReqd = Object.prototype.hasOwnProperty.call(
            obj,
            'isLotSerialReqd'
        )
            ? obj.isLotSerialReqd
            : Object.prototype.hasOwnProperty.call(obj, 'isSerialRequired')
            ? obj.isSerialRequired
            : hasLotSerialData;

        const validatedLotSerialArr =
            hasLotSerialData && validatedProduct
                ? getLotSerialValidationData(obj, validatedProduct)
                : [];
        let isInvalidFeeQty = null,
            linkedSstref = null,
            isInvalidLotSerialMsg = null;
        if (!obj.isAdditionalCartEntry) {
            const validatedItemOldObj = {
                ...obj,
                isUNiDPlanFeeItem: isUNiDPlanFeeItem,
                isReplenishmentRequired: isReplenishmentRequired,
            };
            if (
                obj.isBundleItem &&
                obj.bundleItem &&
                obj.bundleItem.length > 0
            ) {
                const updatedChildrenArr = getRPValidatedChildrenData(
                    existingItems,
                    obj.bundleItem,
                    itemIdx,
                    validatedItems,
                    countryISOCode
                );
                validatedItemOldObj.bundleItem = updatedChildrenArr.filter(
                    (data) => data
                );
            }
            return validatedItemOldObj;
        }
        if (hasLotSerialData) {
            const itemSerialNumber = obj.lotSerialNumber || 
                (obj.lotSerial && obj.lotSerial.length > 0 ? (getArrayItem(obj.lotSerial)?.Serial || getArrayItem(obj.lotSerial)?.lotSerialNumber) : null);
            if (
                validatedLotSerialArr.find(
                    (vLot) =>
                        itemSerialNumber &&
                        itemSerialNumber.length > 0 &&
                        (vLot.lotSerialNumber === itemSerialNumber || vLot.lotSerialNumber ==='unknown') &&
                        (vLot.statusCode === '10712' ||
                            vLot.statusCode === '11902' ||
                            vLot.statusCode === '11903' ||
                            vLot.statusCode === '11904' ||
                            vLot.statusCode === '11907' ||
                            vLot.statusCode === '11905' ||
                            vLot.statusCode === '11906' ||
                            vLot.statusCode === '10826' ||
                            vLot.statusCode === '11909')
                )
            ) {
                const feeItemQtyError = validatedLotSerialArr.find((vLot) => {
                    return vLot.statusCode === '10826';
                });
                if (feeItemQtyError) {
                    isInvalidFeeQty = feeItemQtyError?.message
                        ? feeItemQtyError.message
                        : null;
                } else {
                    const isVendorCaseId = validatedLotSerialArr.find(
                        (vLot) => {
                            return (
                                vLot.statusCode === '11902' ||
                                vLot.statusCode === '11903' ||
                                vLot.statusCode === '11904' ||
                                vLot.statusCode === '11907' ||
                                vLot.statusCode === '11905' ||
                                vLot.statusCode === '11906' ||
                                vLot.statusCode === '11909'
                            );
                        }
                    );
                    isInvalidLotSerialMsg = isVendorCaseId?.message
                        ? isVendorCaseId.message
                        : (obj.isSerialRequired && !obj.isLot) ||
                          obj.salesOrg === 'S030'
                        ? invalidSerialNumberMsg()
                        : invalidLotNumberMsg();
                    linkedSstref =
                        isVendorCaseId?.linkedSstref &&
                        isVendorCaseId.linkedSstref;
                }
            } else if (
                validatedLotSerialArr.find(
                    (vLot) => {
                        // Check if any lotSerial item has a valid Serial value
                        const hasSerialFromArray = obj.lotSerial?.every(ls => ls?.Serial && ls.Serial.length > 0);
                        const serialValue = obj.lotSerialNumber || (hasSerialFromArray ? 'hasValue' : '');
                        // Check if lotSerialUnknown is set at item level or in any lotSerial entry
                        const isUnknown = obj.lotSerialUnknown || obj.lotSerial?.some(ls => ls?.lotSerialUnknown);
                        return (!serialValue || serialValue.length === 0) &&
                        !isUnknown &&
                        (vLot.statusCode === '10712' ||
                            vLot.statusCode === '11902' ||
                            vLot.statusCode === '11903' ||
                            vLot.statusCode === '11904' ||
                            vLot.statusCode === '11907' ||
                            vLot.statusCode === '11905' ||
                            vLot.statusCode === '11906' ||
                            vLot.statusCode === '10826' ||
                            vLot.statusCode === '11909');
                    }
                )
            ) {
                const feeItemQtyError = validatedLotSerialArr.find((vLot) => {
                    return vLot.statusCode === '10826';
                });
                if (feeItemQtyError) {
                    isInvalidFeeQty = feeItemQtyError?.message
                        ? feeItemQtyError.message
                        : null;
                } else if (isLotSerialReqd) {
                    const isVendorCaseId = validatedLotSerialArr.find(
                        (vLot) => {
                            return (
                                vLot.statusCode === '11902' ||
                                vLot.statusCode === '11903' ||
                                vLot.statusCode === '11904' ||
                                vLot.statusCode === '11907' ||
                                vLot.statusCode === '11905' ||
                                vLot.statusCode === '11906' ||
                                vLot.statusCode === '11909'
                            );
                        }
                    );
                    isInvalidLotSerialMsg = isVendorCaseId?.message
                        ? isVendorCaseId.message
                        : (obj.isSerialRequired && !obj.isLot) ||
                          obj.salesOrg === 'S030'
                        ? serialNumberReqdMsg()
                        : lotNumberReqdMsg();
                    linkedSstref =
                        isVendorCaseId?.linkedSstref &&
                        isVendorCaseId.linkedSstref;
                } else {
                    isInvalidLotSerialMsg = null;
                }
            } else {
                isInvalidLotSerialMsg = null;
            }
        } else {
            isInvalidLotSerialMsg = null;
        }
        const validatedItemObj = {
            ...obj,
            isInvalidItemMsg: !validItemsCFN.includes(obj.cfn || obj.productCFN)
                ? invalidProductMsg()
                : null,
            isLotSerialReqd: isLotSerialReqd,
            isInvalidLotSerialMsg: isInvalidLotSerialMsg,
            linkedSstref: linkedSstref,
            isInvalidQtyMsg: isInvalidFeeQty
                ? isInvalidFeeQty
                : isInvalidUnidItemQuantity
                ? invalidUnidRodItemQtyMsg
                : obj.isInvalidQtyMsg,
            isInvalidUnidItemQuantity,
            selectedBatchValue: isInvalidUnidItemQuantity
                ? obj.lotSerialNumber
                : null,
            isUNiDPlanFeeItem: isUNiDPlanFeeItem,
            isReplenishmentRequired: isReplenishmentRequired,
        };
        if (obj.isBundleItem && obj.bundleItem && obj.bundleItem.length > 0) {
            const updatedChildrenArr = getRPValidatedChildrenData(
                existingItems,
                obj.bundleItem,
                itemIdx,
                validatedItems,
                countryISOCode
            );
            validatedItemObj.bundleItem = updatedChildrenArr.filter(
                (data) => data
            );
        }
        return validatedItemObj;
    });
};

export const getNewItemObj = (
    newItem,
    existingItem,
    isNew,
    bundleHeadersArr,
    isChildItem,
    myCaseResourceType,
    scannerQty,

) => {
    const lotSerialArr = newItem?.barcodeData
        ? [
              {
                  Serial: newItem?.barcodeData?.batchOrSerialNumber,
                  ...(scannerQty ? { Used: 1 } : { used: 1 }),
                  id: getUniqueKey(),
              },
          ]
        : [{ Serial: '', used: false, id: getUniqueKey() }];
    let newItemObj = {};

    const itemCFN = newItem.value.toUpperCase();
    // const itemCFN = newItem?.barcodeData
    //     ? newItem?.value?.toUpperCase()
    //     : newItem.value.cfn.toUpperCase();
    if (
        existingItem.isAdditionalCartEntry ||
        existingItem.isAdditionalCartChild
    ) {
        newItemObj = {
            ...existingItem,
            productCFN: itemCFN,
            scannedGTIN: newItem?.scannedGTIN
                ? newItem?.scannedGTIN
                : newItem.GTIN
                ? newItem.GTIN
                : '',
            cfn: itemCFN,
            basePrice: undefined,
            description: '',
            extPrice: undefined,
            salesOrg: '',
            uom: '',
            unitPrice: undefined,
            lotSerialNumber: newItem?.barcodeData
                ? newItem?.barcodeData?.batchOrSerialNumber
                : '',
            usedQuantity:
                bundleHeadersArr?.includes(itemCFN) && existingItem.usedQuantity
                    ? existingItem.usedQuantity
                    : '',
            isInvalidItemMsg: null,
            isEditable: false,
            isPriced: false,
            isBundleItem: bundleHeadersArr?.includes(itemCFN),
            bundleItem: bundleHeadersArr?.includes(itemCFN)
                ? existingItem.bundleItem
                : undefined,
            noItemInBundleErr: null,
            isInvalidLotSerialMsg: null,
            isInvalidQtyMsg: null,
        };
    } else {
        newItemObj = {
            cfn: itemCFN,
            scannedGTIN: newItem?.scannedGTIN
                ? newItem?.scannedGTIN
                : newItem.value.GTIN
                ? newItem.value.GTIN
                : '',
            dailyRate: '',
            description: newItem?.barcodeData
                ? newItem?.barcodeData?.productDetails?.description
                : '',
            salesOrg: newItem?.barcodeData
                ? newItem?.barcodeData?.productDetails?.salesOrg
                : '',
            uom: newItem?.barcodeData
                ? newItem?.barcodeData?.productDetails?.uom
                : '',
            accountOwned: false,
            usedQuantity: newItem?.barcodeData ? '1' : '',
            lotSerial: lotSerialArr,
            otherQtyExtPrice: getTotalExtendedPrice('', lotSerialArr, 'other'),
            usedQtyExtPrice: getTotalExtendedPrice('', lotSerialArr, 'used'),
            extPrice: getTotalExtendedPrice('', lotSerialArr, 'total'),
            isInvalidItemMsg: null,
            isAdditionalCartEntry: false,
            unqId: `${getUniqueKey()} ${itemCFN}`,
            isEditable: false,
        }; 
    }

    if (
        myCaseResourceType !== 'reportPayment' &&
        (newItem.isBundle || bundleHeadersArr?.includes(itemCFN))
    ) {
        newItemObj.isBundleItem = true;
        newItemObj.isBundleFromAddItem = true;
        newItemObj.isBundleExpanded = true;
        newItemObj.lotSerial =
            //  newItem?.barcodeData
            // ?[
            //     {
            //         Serial: newItem?.barcodeData?.batchOrSerialNumber,
            //         used:  '1',
            //         id: getUniqueKey(),
            //         isInvalidQtyMsg: null,
            //     },
            // ] :
            [
                {
                    used: isChildItem ? '' : '1',
                    isInvalidQtyMsg: null,
                    id: getUniqueKey(),
                },
            ];
        newItemObj.isInvalidItemMsg = newItemObj.isAdditionalCartEntry
            ? t('reportUsage.procedure.unidSurgery.SurgeryOrReport.notadded')
            : isChildItem
            ? t('equity.common.utils.bundleItem')
            : bundleHeadersArr?.includes(itemCFN) && itemCFN.startsWith('M1')
            ? invalidBundleItemMsg()
            : null;
        if (!isChildItem) {
            newItemObj.children = isNew
                ? []
                : existingItem.children &&
                  newItemObj.isInvalidItemMsg !== invalidBundleItemMsg()
                ? existingItem.children
                : [];
        }
    } else if (
        myCaseResourceType === 'reportPayment' &&
        (newItem.isBundle || bundleHeadersArr?.includes(itemCFN))
    ) {
        newItemObj.isBundleItem = true;
        newItemObj.isBundleFromAddItem = true;
        newItemObj.isBundleExpanded = true;
        newItemObj.isInvalidItemMsg = isChildItem
            ? t('equity.common.utils.bundleItem')
            : bundleHeadersArr?.includes(itemCFN) && itemCFN.startsWith('M1')
            ? invalidBundleItemMsg()
            : null;
        if (!isChildItem) {
            newItemObj.bundleItem = isNew
                ? []
                : existingItem.bundleItem &&
                  newItemObj.isInvalidItemMsg !== invalidBundleItemMsg()
                ? existingItem.bundleItem
                : [];
        }
    }
    if (existingItem.payment) {
        newItemObj.payment = existingItem.payment;
    }
    return newItemObj;
};

export const getReplenNonTissueItems = (
    productsList,
    myCaseResourceType,
    countryISOCode
) => {
    let nonTissueItems = productsList.filter(
        (x) =>
            x.salesOrg !== 'S030' &&
            !x.isBundleItem &&
            !x.isUnidRodItem &&
            x.materialType !== 'DIEN' &&
            x.isReplenishmentRequired
    );
    const bundleItems = productsList.filter(
        (item) => item.isBundleItem && item.children && item.children.length > 0
    );
    const rpBundleItems = productsList.filter(
        (item) =>
            item.isBundleItem && item.bundleItem && item.bundleItem.length > 0
    );
    const bundleChildren =
        myCaseResourceType === 'reportPayment'
            ? rpBundleItems && rpBundleItems.length > 0
                ? rpBundleItems
                      .reduce((prev, curr) => prev.concat(curr.bundleItem), [])
                      .filter(
                          (x) =>
                              x.salesOrg !== 'S030' &&
                              x.materialType !== 'DIEN' &&
                              x.isReplenishmentRequired
                      )
                : []
            : bundleItems && bundleItems.length > 0
            ? bundleItems
                  .reduce((prev, curr) => prev.concat(curr.children), [])
                  .filter(
                      (x) =>
                          x.salesOrg !== 'S030' &&
                          !x.isUnidRodItem &&
                          x.materialType !== 'DIEN' &&
                          x.isReplenishmentRequired
                  )
            : [];
    nonTissueItems = nonTissueItems.concat(bundleChildren);
    return nonTissueItems;
};

export const getValidReplenishGridData = (
    countryISOCode,
    productsList,
    existingReplenData,
    draftOrderReplenItems,
    validatedReplenData,
    myCaseResourceType
    // requestDeliveryDate
) => {
    const itemsThatCanBeReplenished = [];
    const nonTissueItems = getReplenNonTissueItems(
        productsList,
        myCaseResourceType,
        countryISOCode
    );
    if (nonTissueItems.length > 0) {
        nonTissueItems.forEach((item) => {
            if (!item.cfn && item.productCFN) {
                item.cfn = item.productCFN;
            }
            if (
                !itemsThatCanBeReplenished.map((x) => x.cfn).includes(item.cfn)
            ) {
                let shippingDetails = null,
                    newReplenQty;
                const draftRreplenItemIdx =
                    draftOrderReplenItems &&
                    draftOrderReplenItems.findIndex(
                        (data) => data.productCFN === item.cfn
                    );
                if (existingReplenData.map((x) => x.cfn).includes(item.cfn)) {
                    const replenItemIdx = existingReplenData.findIndex(
                        (data) => data.cfn === item.cfn
                    );
                    if (
                        replenItemIdx >= 0 &&
                        existingReplenData[replenItemIdx]
                    ) {
                        if (existingReplenData[replenItemIdx].shippingDetails)
                            shippingDetails =
                                existingReplenData[replenItemIdx]
                                    .shippingDetails;
                        if (existingReplenData[replenItemIdx].newReplenQty)
                            newReplenQty =
                                existingReplenData[replenItemIdx].newReplenQty;
                    }
                } else if (
                    draftRreplenItemIdx >= 0 &&
                    draftOrderReplenItems[draftRreplenItemIdx] &&
                    draftOrderReplenItems[draftRreplenItemIdx]
                        .replenishmentDetails &&
                    draftOrderReplenItems[draftRreplenItemIdx]
                        .replenishmentDetails.address
                ) {
                    newReplenQty = draftOrderReplenItems[draftRreplenItemIdx]
                        .replenishmentDetails.replenishQuantity
                        ? parseInt(
                              draftOrderReplenItems[draftRreplenItemIdx]
                                  .replenishmentDetails.replenishQuantity
                          )
                        : undefined;
                    shippingDetails = {
                        accountDetails: {
                            accountId:
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.accountId,
                        },
                        shippingAddress: {
                            accountName:
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.shipToName &&
                                draftOrderReplenItems[
                                    draftRreplenItemIdx
                                ].replenishmentDetails.shipToName
                                    .replace(
                                        draftOrderReplenItems[
                                            draftRreplenItemIdx
                                        ].replenishmentDetails.shipTo,
                                        ''
                                    )
                                    .trim(),
                            addressLine1:
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.address.line1,
                            city: draftOrderReplenItems[draftRreplenItemIdx]
                                .replenishmentDetails.address.city,
                            postalCode:
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.address.postalCode,
                            region: draftOrderReplenItems[draftRreplenItemIdx]
                                .replenishmentDetails.address.region,
                            shippingAddressId:
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.shippingAddressId ||
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.shipTo,
                        },
                        shippingMethod: {
                            shippingMethodId:
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.shippingMethodId,
                            shippingMethodName:
                                draftOrderReplenItems[draftRreplenItemIdx]
                                    .replenishmentDetails.shippingMethodName,
                        },
                        attnLine: draftOrderReplenItems[draftRreplenItemIdx]
                            .replenishmentDetails.attentionLine
                            ? draftOrderReplenItems[draftRreplenItemIdx]
                                  .replenishmentDetails.attentionLine
                            : '',
                        careOfField: draftOrderReplenItems[draftRreplenItemIdx]
                            .replenishmentDetails?.careOfField
                            ? draftOrderReplenItems[draftRreplenItemIdx]
                                  .replenishmentDetails?.careOfField
                            : '',
                        transferRecipient: draftOrderReplenItems[
                            draftRreplenItemIdx
                        ].replenishmentDetails?.transferRecipient
                            ? draftOrderReplenItems[draftRreplenItemIdx]
                                  .replenishmentDetails?.transferRecipient
                            : '',
                        requestDeliveryDate: draftOrderReplenItems[
                            draftRreplenItemIdx
                        ].replenishmentDetails.requestedDeliveryDate
                            ? normalizeToLocalCalendarDate(
                                  draftOrderReplenItems[
                                      draftRreplenItemIdx
                                  ].replenishmentDetails.requestedDeliveryDate
                              )
                            : '',
                        surgeryDate: draftOrderReplenItems[draftRreplenItemIdx]
                            .replenishmentDetails.surgeryDate
                            ? normalizeToLocalCalendarDate(
                                  draftOrderReplenItems[
                                      draftRreplenItemIdx
                                  ].replenishmentDetails.surgeryDate
                              )
                            : '',
                        surgeryNumber: draftOrderReplenItems[
                            draftRreplenItemIdx
                        ].replenishmentDetails.surgeryNumber
                            ? draftOrderReplenItems[draftRreplenItemIdx]
                                  .replenishmentDetails.surgeryNumber
                            : '',
                        billToId:
                            draftOrderReplenItems[draftRreplenItemIdx]
                                .replenishmentDetails.billToId,
                        physicianId:
                            draftOrderReplenItems[draftRreplenItemIdx]
                                .replenishmentDetails.physicianId,
                    };
                }
                // } else if (
                //     countryISOCode === 'JP' &&
                //     requestDeliveryDate !== '' &&
                //     requestDeliveryDate !== undefined
                // ) {
                //     shippingDetails.requestDeliveryDate = requestDeliveryDate;
                // }

                const validatedItem =
                    validatedReplenData?.length > 0 &&
                    validatedReplenData.find(
                        (vItem) => vItem.productCFN === item.cfn
                    );
                itemsThatCanBeReplenished.push({
                    ...item,
                    isSelected: validatedItem?.isExcluded ? false : true,
                    isExcludedItem: validatedItem?.isExcluded ? true : false,
                    replenQty:
                        myCaseResourceType === 'reportPayment'
                            ? (item.usedQuantity !== undefined ? item.usedQuantity : getTotalQuantity(item.lotSerial))
                            : getTotalQuantity(item.lotSerial),

                    shippingDetails,
                    newReplenQty,
                });
            } else {
                const itemIdx = itemsThatCanBeReplenished.findIndex(
                    (x) => x.cfn === item.cfn
                );
                if (itemIdx >= 0) {
                    const itemUsedQty = item.usedQuantity !== undefined ? item.usedQuantity : getTotalQuantity(item.lotSerial);
                    const existingReplenQty = itemsThatCanBeReplenished[itemIdx].replenQty;
                    itemsThatCanBeReplenished[itemIdx].replenQty =
                        myCaseResourceType === 'reportPayment'
                            ? existingReplenQty
                                ? parseInt(existingReplenQty) + (parseInt(itemUsedQty) || 0)
                                : (parseInt(itemsThatCanBeReplenished[itemIdx].usedQuantity) || 0) + (parseInt(itemUsedQty) || 0)
                            : itemsThatCanBeReplenished[itemIdx].replenQty
                            ? parseInt(
                                  itemsThatCanBeReplenished[itemIdx].replenQty
                              ) + getTotalQuantity(item.lotSerial)
                            : getTotalQuantity([
                                  ...itemsThatCanBeReplenished[itemIdx]
                                      .lotSerial,
                                  ...item.lotSerial,
                              ]);
                }
            }
        });
    }

    itemsThatCanBeReplenished.forEach((rItem, rIdx) => {
        const replenNTItems = nonTissueItems?.filter(
            (nt) => nt.cfn === rItem.productCFN
        );
        if (
            replenNTItems?.length > 1 &&
            replenNTItems.some((x) => x.accountOwned) &&
            !replenNTItems.every((y) => y.accountOwned)
        ) {
            const totalAccOwnedItemsQty = getTotalQuantity(
                flattenLotSerialArrayOfArrays('accOwned', replenNTItems)
            );
            itemsThatCanBeReplenished[rIdx].accountOwned = false;
            itemsThatCanBeReplenished[rIdx].replenQty =
                rItem.replenQty -
                parseInt(totalAccOwnedItemsQty ? totalAccOwnedItemsQty : 0);
        }
    });

    return itemsThatCanBeReplenished;
};

export const getUpdatedPriceOverridenData = (
    existingTableData,
    priceOverridenData
) => {
    return existingTableData.map((existingItem) => {
        let updatedItemObj = { ...existingItem };
        const priceOverwrittenItemIdx = priceOverridenData.findIndex((y) =>
            y.unqId
                ? y.unqId === existingItem.unqId
                : y.cfn === existingItem.cfn
        );
        if (priceOverwrittenItemIdx >= 0) {
            const overrideItem = priceOverridenData[priceOverwrittenItemIdx];

            // Merge lotSerial: preserve Serial/lotSerialNumber from existingItem but keep quantities from overrideItem
            // This is critical for tissue products (S030) where lot/serial must be preserved after price override
            let mergedLotSerial = overrideItem.lotSerial;
            const existingLotSerial = existingItem.lotSerial;
            const overrideLotSerial = overrideItem.lotSerial;
            
            if (existingLotSerial && existingLotSerial.length > 0 && overrideLotSerial && overrideLotSerial.length > 0) {
                // If overrideItem has empty Serial but existingItem has it, merge them
                if (!getArrayItem(overrideLotSerial)?.Serial && !getArrayItem(overrideLotSerial)?.lotSerialNumber && 
                    (getArrayItem(existingLotSerial)?.Serial || getArrayItem(existingLotSerial)?.lotSerialNumber)) {
                    mergedLotSerial = existingLotSerial.map((existingLot, idx) => {
                        const overrideLot = overrideLotSerial[idx] || {};
                        return {
                            ...existingLot,
                            // Preserve quantities from override if available, otherwise keep existing
                            used: overrideLot.used !== undefined ? overrideLot.used : existingLot.used,
                            other: overrideLot.other !== undefined ? overrideLot.other : existingLot.other,
                            Used: overrideLot.Used !== undefined ? overrideLot.Used : existingLot.Used,
                            Other: overrideLot.Other !== undefined ? overrideLot.Other : existingLot.Other,
                        };
                    });
                }
            } else if (existingLotSerial && existingLotSerial.length > 0 && (!overrideLotSerial || overrideLotSerial.length === 0)) {
                mergedLotSerial = existingLotSerial;
            }

            updatedItemObj = {
                ...overrideItem,
                lotSerial: existingItem.lotSerial ?? overrideItem.lotSerial,
                lotSerialNumber:
                    existingItem.lotSerialNumber ??
                    overrideItem.lotSerialNumber,
                payment: existingItem.payment ?? overrideItem.payment,
                isPoImageError: existingItem.isPoImageError ?? overrideItem.isPoImageError,
                poImageErrorMsg: existingItem.poImageErrorMsg ?? overrideItem.poImageErrorMsg,
                // Use merged lotSerial that preserves Serial from existing and quantities from override
                lotSerial: mergedLotSerial,
                lotSerialNumber: (existingItem.lotSerialNumber || getArrayItem(existingItem.lotSerial)?.Serial || getArrayItem(existingItem.lotSerial)?.lotSerialNumber) 
                    || (overrideItem.lotSerialNumber || getArrayItem(overrideItem.lotSerial)?.Serial || ''),
                // Preserve usedQuantity/otherQuantity - use override values if available, fall back to existing
                usedQuantity: overrideItem.usedQuantity !== undefined ? overrideItem.usedQuantity : existingItem.usedQuantity,
                otherQuantity: overrideItem.otherQuantity !== undefined ? overrideItem.otherQuantity : existingItem.otherQuantity,
                // Clear validation errors as data has been updated through price override
                isInvalidQtyMsg: null,
            };
        }
        return updatedItemObj;
    });
};

export const getProductsArrFromReportPaymentFlow = (productsInUsageObj) => {
    if (!productsInUsageObj || !Array.isArray(productsInUsageObj) || productsInUsageObj.length === 0) {
        return [];
    }
    
    return productsInUsageObj.concat(
        productsInUsageObj
            .filter((item) => item.bundleItem?.length > 0)
            .reduce(
                (prev, curr) =>
                    prev.concat(
                        curr.bundleItem?.length > 0
                            ? curr.bundleItem.map((child) => ({
                                  ...child,
                                  usedQuantity: child.quantity,
                              }))
                            : []
                    ),
                []
            )
    );
};

/**
 * Report Payment: split a newly-added item that has multiple lot/serials (or a single
 * lot/serial carrying both Used and Other) into SEPARATE grid item objects — one per
 * (lot/serial x qty-type) combination that has an entered quantity. Each resulting object
 * is a full clone with a SINGLE lot/serial entry carrying ONLY used OR ONLY other, plus
 * item-level usedQuantity XOR otherQuantity so it renders/prices like an existing RP line.
 * Empty sides (0/blank) are skipped. Non-newly-added, bundle, or single-line items are
 * returned unchanged.
 * @param {Array} gridData reportPaymentGridData
 * @returns {Array} grid data with eligible items expanded into per-qty-type rows
 */
export const splitRPItemsByLotSerialQty = (gridData = []) => {
    if (!Array.isArray(gridData) || gridData.length === 0) {
        return gridData;
    }
    let splitSeq = 0;
    const buildSplitObj = (item, lot, qtyType, qtyVal) => {
        splitSeq += 1;
        const uniqueSuffix = `${getUniqueKey()}-${splitSeq}`;
        const serialVal =
            lot?.lotSerialNumber || lot?.Serial || item.lotSerialNumber || '';
        // Qty-required for split rows is derived from the missing qty at render/
        // button-eval time (not stamped here) so it stays in sync after pricing.
        const qtyValidationMsg = null;
        const singleLot = {
            Serial: serialVal,
            lotSerialNumber: serialVal,
            id: uniqueSuffix,
            lotSerialUnknown: lot?.lotSerialUnknown,
            isInvalidQtyMsg: qtyValidationMsg,
            ...(qtyType === 'used'
                ? { used: qtyVal }
                : { other: qtyVal }),
        };
        return {
            ...item,
            id: uniqueSuffix,
            isSelected: false,
            lotSerial: [singleLot],
            lotSerialNumber: serialVal,
            usedQuantity: qtyType === 'used' ? qtyVal : undefined,
            otherQuantity: qtyType === 'other' ? qtyVal : undefined,
            adjustedUsedQtyPrice:
                qtyType === 'used' ? item.adjustedUsedQtyPrice : undefined,
            adjustedOtherQtyPrice:
                qtyType === 'other' ? item.adjustedOtherQtyPrice : undefined,
            unqId: `${uniqueSuffix} ${item.cfn || item.productCFN}`,
            isSplitLotSerialItem: true,
            splitQtyType: qtyType,
            isInvalidQtyMsg: qtyValidationMsg,
        };
    };
    const result = [];
    gridData.forEach((item) => {
        const lotSerialArr = item?.lotSerial;
        const isEligible =
            item?.isAdditionalCartEntry &&
            !item?.isBundleItem &&
            !item?.isSplitLotSerialItem &&
            Array.isArray(lotSerialArr) &&
            lotSerialArr.length > 0;
        if (!isEligible) {
            result.push(item);
            return;
        }
        const splitRows = [];
        const isTissue = item?.salesOrg === 'S030';
        lotSerialArr.forEach((lot) => {
            let usedVal = lot?.Used ?? lot?.used;
            let otherVal = lot?.Other ?? lot?.other;
            // Fallback: when a single lot/serial carries no lot-level qty, use the
            // item-level usedQuantity/otherQuantity (some add flows store the qty
            // at item level instead of on the lotSerial entry).
            // For tissue items (S030), do NOT fall back when the value is explicitly `false`
            // because `false` means the checkbox was intentionally unchecked.
            if (lotSerialArr.length === 1) {
                const usedIsUnset = usedVal === undefined || usedVal === null || usedVal === '';
                const otherIsUnset = otherVal === undefined || otherVal === null || otherVal === '';
                // For non-tissue items, also fall back when value is `false`
                const usedNeedsFallback = isTissue ? usedIsUnset : (usedIsUnset || usedVal === false);
                const otherNeedsFallback = isTissue ? otherIsUnset : (otherIsUnset || otherVal === false);
                if (usedNeedsFallback) {
                    usedVal = item?.usedQuantity;
                }
                if (otherNeedsFallback) {
                    otherVal = item?.otherQuantity;
                }
            }
            // S030 (tissue): split into ONE part only - whichever checkbox is selected (used OR other)
            // S002 (non-tissue): split into TWO parts (used AND other), default qty = 0
            if (isTissue) {
                // For tissue, checkbox values could be true, 1, or other truthy values
                // Also check for any truthy value to handle edge cases where value might be set differently
                const isUsedSelected = usedVal === true || usedVal === 1 || usedVal === '1' || (usedVal && usedVal !== false);
                const isOtherSelected = otherVal === true || otherVal === 1 || otherVal === '1' || (otherVal && otherVal !== false);
                if (isUsedSelected) {
                    splitRows.push(buildSplitObj(item, lot, 'used', 1));
                } else if (isOtherSelected) {
                    splitRows.push(buildSplitObj(item, lot, 'other', 1));
                }
                // If neither checkbox is selected, don't split - keep original item
            } else {
                // Non-tissue (S002): always create both splits, default to 0 if no qty
                const usedQty = (usedVal !== undefined && usedVal !== null && usedVal !== '' && usedVal !== false)
                    ? usedVal
                    : 0;
                const otherQty = (otherVal !== undefined && otherVal !== null && otherVal !== '' && otherVal !== false)
                    ? otherVal
                    : 0;
                splitRows.push(buildSplitObj(item, lot, 'used', usedQty));
                splitRows.push(buildSplitObj(item, lot, 'other', otherQty));
            }
        });
        // Convert to read-only split rows whenever at least one qty side was
        // entered (even a single used-only or other-only item). Only keep the
        // original editable item when nothing qualified.
        if (splitRows.length >= 1) {
            result.push(...splitRows);
        } else {
            result.push(item);
        }
    });
    return result;
};

export const buildPriceItemsRequest = ({
    tableData = [],
    bundleHeadersArr = [],
    reportPaymentTable = false,
    usageDetails = {},
    myCaseRefId,
    myCaseRefIdObj,
}) => {
    const validReportUsageItems = tableData.filter((item) => {
        const value = (item.cfn || item.productCFN || '').toUpperCase();
        return !(
            value.startsWith('M1') &&
            bundleHeadersArr?.includes(item.cfn || item.productCFN)
        );
    });

    const bundleItemsInTableData = (tableData && Array.isArray(tableData))
        ? tableData.filter(
            (item) => item.children && item.children.length > 0
          )
        : [];

    const bundleChildItems = reportPaymentTable
        ? (tableData && Array.isArray(tableData) && tableData.filter((item) => item.bundleItem?.length > 0)?.length > 0)
            ? tableData
                  .filter((item) => item.bundleItem?.length > 0)
                  .reduce((prev, curr) => prev.concat(curr.bundleItem), [])
            : []
        : bundleItemsInTableData &&
          bundleItemsInTableData.reduce((previousValue, currentValue) => {
              const childItems = currentValue.children.filter(
                  (child) =>
                      !bundleHeadersArr?.includes(
                          (child.cfn || child.productCFN || '').toUpperCase()
                      )
              );
              return previousValue.concat(childItems);
          }, []);

    const itemsToBePriced =
        bundleChildItems && bundleChildItems.length > 0
            ? validReportUsageItems.concat(bundleChildItems)
            : validReportUsageItems;

    const itemCFNs = itemsToBePriced
        .map((item) => item.cfn || item.productCFN)
        .filter(Boolean);

    const priceItemsObj = {
        productCFN: itemCFNs.toString(),
        sapPartnerId: usageDetails.accountId,
        sstref: myCaseRefId || myCaseRefIdObj || usageDetails?.myCaseRef,
    };

    if (usageDetails.territoryId) {
        priceItemsObj.territoryId = usageDetails.territoryId;
    }

    if (usageDetails.changeAccountEnabled) {
        priceItemsObj.isChangeAccount = true;
    }

    return {
        itemsToBePriced,
        itemCFNs,
        priceItemsObj,
    };
};

export const modifyDraftProductsResponse = (
    existingItems,
    bundleHeadersArr,
    deliveredRodItems,
    isInReportPaymentScreen
) => {
    const itemTableData = [];
    existingItems.forEach((item, idx) => {
        if (
            !itemTableData
                .map((x) => (x.unqId ? x.unqId : x.cfn))
                .includes(item.unqId ? item.unqId : item.productCFN) ||
            (isInReportPaymentScreen && item.parentCFN)
        ) {
            const itemObj = {
                cfn: item.productCFN,
                accountOwned: item.isAccountOwned,
                userMadeAccountOwned: item.isAccountOwned,
                uom: item.uom,
                description: item.description,
                salesOrg: item.salesOrg,
                isLot: item.isLot,
                isSerialRequired: item.isSerialRequired,
                materialType: item.materialType,
                showCheckBox: item.showCheckBox || false,
                unqId: item.unqId
                    ? item.unqId
                    : `${getUniqueKey() + idx} ${item.productCFN}`,
                isDienFeeItem: item.isDienFeeItem || false,
            };
            if (item.scannedGTIN) {
                itemObj.scannedGTIN = item.scannedGTIN;
            }
            if (item.overrideUnitPrice) {
                itemObj.overridenUnitPriceApplied = true;
                itemObj.isOverrideApplied = true;
            }
            const unidItem = deliveredRodItems?.filter(
                (uItem) =>
                    uItem.productCFN === item.productCFN &&
                    uItem.itemBatchAndQty &&
                    uItem.itemBatchAndQty.length > 0
            );
            itemObj.isUnidRodItem =
                item.isUnidRodItem ||
                (unidItem?.length > 0 && getArrayItem(unidItem)?.isUnidRodItem)
                    ? true
                    : null;
            if (
                itemObj.isUnidRodItem &&
                deliveredRodItems?.length > 0 &&
                unidItem &&
                getArrayItem(unidItem)
            ) {
                itemObj.batchQtyObjArr = getArrayItem(unidItem).itemBatchAndQty;
            }
            if (
                bundleHeadersArr &&
                bundleHeadersArr.includes(item.productCFN)
            ) {
                itemObj.isBundleItem = true;
                itemObj.children = [];
                itemObj.isBundleExpanded = true;
                itemObj.bundlePosition = item.bundlePosition;
            }
            itemObj.basePrice = item.unitPrice
                ? setCurrencyFormat(item.unitPrice)
                : null;
            if (item.isPriceNotAvailable) {
                itemObj.isPriceNotAvailable = item.isPriceNotAvailable;
            }
            if (item.description) {
                itemObj.description = item.description;
            }
            if (item.isAdditionalCartEntry) {
                itemObj.isAdditionalCartEntry = item.isAdditionalCartEntry;
            }
            // Report Payment: keep the read-only split marker + item-level qty so the
            // rebuilt filteredProduct still renders per-(lotSerial x qtyType) split rows
            // read-only (both desktop and mobile read these fields). Without this, the
            // rebuild dropped the marker/qty and the card reverted to an editable input.
            if (item.isSplitLotSerialItem) {
                itemObj.isSplitLotSerialItem = true;
                itemObj.splitQtyType = item.splitQtyType;
                itemObj.usedQuantity = item.usedQuantity;
                itemObj.otherQuantity = item.otherQuantity;
            }
            if (item.uom) {
                itemObj.uom = item.uom;
            }
            const lotSerialObj = {
                Serial: item.lotSerialNumber,
                lotSerialUnknown:item?.lotSerialUnknown,
                id: getUniqueKey() + idx,
            };
            if (item.usedQuantity === true || parseInt(item.usedQuantity) > 0) {
                lotSerialObj.used =
                    item.salesOrg === 'S030'||item?.showCheckBox ? true : item.usedQuantity;
                if (item.overrideUnitPrice) {
                    itemObj.adjustedUsedQtyPrice = item.overrideUnitPrice;
                }
                if (item.payment) {
                    lotSerialObj.usedQtyPayment = item.payment;
                    itemObj.payment = item.payment;
                }
            } else {
                lotSerialObj.used = false;
            }
            if (
                item.otherQuantity === true ||
                parseInt(item.otherQuantity) > 0
            ) {
                lotSerialObj.other =
                    item.salesOrg === 'S030'||item?.showCheckBox ? true : item.otherQuantity;
                if (item.overrideUnitPrice) {
                    itemObj.adjustedOtherQtyPrice = item.overrideUnitPrice;
                }
                if (item.payment) {
                    lotSerialObj.otherQtyPayment = item.payment;
                    itemObj.payment = item.payment;
                }
            }
            // Preserve existing lotSerial array if it has multiple entries, otherwise create new one
            if (item.lotSerial && item.lotSerial.length > 0) {
                itemObj.lotSerial = item.lotSerial.map((ls) => {
                    const updatedLotSerial = { ...ls };
                    const lotSerialMatches =
                        item.lotSerial.length === 1 ||
                        !item.lotSerialNumber ||
                        updatedLotSerial.Serial === item.lotSerialNumber ||
                        updatedLotSerial.lotSerialNumber === item.lotSerialNumber;
                    if (item.payment && lotSerialMatches) {
                        if (item.usedQuantity) {
                            updatedLotSerial.usedQtyPayment =
                                updatedLotSerial.usedQtyPayment || item.payment;
                        }
                        if (item.otherQuantity) {
                            updatedLotSerial.otherQtyPayment =
                                updatedLotSerial.otherQtyPayment || item.payment;
                        }
                    }
                    return updatedLotSerial;
                });
            } else {
                itemObj.lotSerial = [lotSerialObj];
            }

            if (item.parentCFN) {
                itemObj.parentCFN = item.parentCFN;
                itemObj.parentId = item.parentId;
                itemObj.bundlePosition = item.bundlePosition;
            } else {
                itemObj.extPrice = getTotalExtendedPrice(
                    itemObj.basePrice ? itemObj.basePrice : '',
                    itemObj.lotSerial,
                    'total'
                );
                itemObj.otherQtyExtPrice = getTotalExtendedPrice(
                    itemObj.basePrice ? itemObj.basePrice : '',
                    itemObj.lotSerial,
                    'other'
                );
                itemObj.usedQtyExtPrice = getTotalExtendedPrice(
                    itemObj.basePrice ? itemObj.basePrice : '',
                    itemObj.lotSerial,
                    'used'
                );
            }
            itemTableData.push(itemObj);
        } else {
            const itemIdx = itemTableData.findIndex((x) =>
                x.unqId ? x.unqId === item.unqId : x.cfn === item.productCFN
            );
            if (itemIdx >= 0) {
                // Resolve the serial to match against: prefer item.lotSerialNumber,
                // fall back to the Serial inside item.lotSerial[0] (RP grid items
                // store it there), or the first existing entry when neither is available.
                const serialToMatch = item.lotSerialNumber
                    || getArrayItem(item.lotSerial)?.Serial
                    || getArrayItem(itemTableData[itemIdx].lotSerial)?.Serial;
                const lotSerialIdx = itemTableData[
                    itemIdx
                ].lotSerial?.findIndex(
                    (obj) => obj.Serial === serialToMatch
                );
                if (lotSerialIdx >= 0) {
                    if (item.usedQuantity) {
                        if (
                            !itemTableData[itemIdx].lotSerial[lotSerialIdx].used
                        ) {
                            itemTableData[itemIdx].lotSerial[
                                lotSerialIdx
                            ].used = item.usedQuantity;
                        }
                        if (item.payment) {
                            itemTableData[itemIdx].lotSerial[
                                lotSerialIdx
                            ].usedQtyPayment = item.payment;
                            itemTableData[itemIdx].payment = item.payment;
                        }
                        if (item.overrideUnitPrice) {
                            itemTableData[itemIdx].adjustedUsedQtyPrice =
                                item.overrideUnitPrice;
                        }
                    }
                    if (item.otherQuantity) {
                        if (
                            !itemTableData[itemIdx].lotSerial[lotSerialIdx]
                                .other
                        ) {
                            itemTableData[itemIdx].lotSerial[
                                lotSerialIdx
                            ].other = item.otherQuantity;
                        }
                        if (item.payment) {
                            itemTableData[itemIdx].lotSerial[
                                lotSerialIdx
                            ].otherQtyPayment = item.payment;
                            itemTableData[itemIdx].payment = item.payment;
                        }
                        if (item.overrideUnitPrice) {
                            itemTableData[itemIdx].adjustedOtherQtyPrice =
                                item.overrideUnitPrice;
                        }
                    }
                } else {
                    const nextLotSerialObj = {
                        used: item.usedQuantity ? item.usedQuantity : false,
                        id: getUniqueKey() + 1,
                    };
                    if (item.usedQuantity && item.overrideUnitPrice) {
                        itemTableData[itemIdx].adjustedUsedQtyPrice =
                            item.overrideUnitPrice;
                    }
                    if (item.usedQuantity && item.payment) {
                        nextLotSerialObj.usedQtyPayment = item.payment;
                        itemTableData[itemIdx].payment = item.payment;
                    }
                    if (item.otherQuantity) {
                        nextLotSerialObj.other = item.otherQuantity;
                        if (item.payment) {
                            nextLotSerialObj.otherQtyPayment = item.payment;
                            itemTableData[itemIdx].payment = item.payment;
                        }
                        if (item.overrideUnitPrice) {
                            itemTableData[itemIdx].adjustedOtherQtyPrice =
                                item.overrideUnitPrice;
                        }
                    }
                    if (item.lotSerialNumber) {
                        nextLotSerialObj.Serial = item.lotSerialNumber;
                    }
                    itemTableData[itemIdx].lotSerial.push(nextLotSerialObj);
                }
                if (!itemTableData[itemIdx]?.parentCFN) {
                    itemTableData[itemIdx].extPrice = getTotalExtendedPrice(
                        itemTableData[itemIdx].basePrice
                            ? itemTableData[itemIdx].basePrice
                            : '',
                        itemTableData[itemIdx].lotSerial,
                        'total'
                    );
                    itemTableData[itemIdx].otherQtyExtPrice =
                        getTotalExtendedPrice(
                            itemTableData[itemIdx].basePrice
                                ? itemTableData[itemIdx].basePrice
                                : '',
                            itemTableData[itemIdx].lotSerial,
                            'other'
                        );
                    itemTableData[itemIdx].usedQtyExtPrice =
                        getTotalExtendedPrice(
                            itemTableData[itemIdx].basePrice
                                ? itemTableData[itemIdx].basePrice
                                : '',
                            itemTableData[itemIdx].lotSerial,
                            'used'
                        );
                }
            }
        }
    });

    const bundleChildItems = itemTableData.filter(
        (item) => item.parentCFN?.length > 0
    );
    return itemTableData
        .filter((x) => !x.parentCFN)
        ?.map((product) => {
            const productObj = { ...product };
            if (product.isBundleItem && bundleChildItems?.length > 0) {
                productObj.children = bundleChildItems.filter(
                    (child) =>
                        child.parentCFN === product.cfn &&
                        (child?.parentId
                            ? child?.parentId === product?.unqId
                            : child?.bundlePosition
                            ? child?.bundlePosition === product?.bundlePosition
                            : true)
                );
            }
            return productObj;
        });
};

const getAdditionalProcedureDraftData = (
    draftAddProcedureData,
    additionalProcedureData
) => {
    if (typeof draftAddProcedureData.isMazorXRoboticsUsed === 'boolean') {
        const mazorxRoboticsIdx = additionalProcedureData.findIndex(
            (x) => x.description === t('equity.common.utils.mazorX')
        );
        if (mazorxRoboticsIdx >= 0) {
            additionalProcedureData[mazorxRoboticsIdx].isSelected =
                draftAddProcedureData.isMazorXRoboticsUsed;
            if (draftAddProcedureData.mazorKitCode) {
                additionalProcedureData[mazorxRoboticsIdx].mazorKitCode =
                    draftAddProcedureData.mazorKitCode;
            }
            if (draftAddProcedureData.mazorCaseCoverage) {
                additionalProcedureData[mazorxRoboticsIdx].mazorCaseCoverage =
                    draftAddProcedureData.mazorCaseCoverage;
            }
        }
    }
    //Creating a Stealth AXiS object from drafted values..
    if (typeof draftAddProcedureData.isStealthAxis === 'boolean') {
        const stealthAxisIdx = additionalProcedureData.findIndex(
            (x) => x.description === t('equity.common.utils.stealthAxis')
        );
        if (stealthAxisIdx >= 0) {
            additionalProcedureData[stealthAxisIdx].isSelected =
                draftAddProcedureData.isStealthAxis;
            if (draftAddProcedureData?.stealthAxisCode) {
                additionalProcedureData[stealthAxisIdx].stealthAxisCode =
                    draftAddProcedureData.stealthAxisCode;
            }
            if (draftAddProcedureData?.stealthAxisCaseCoverage) {
                additionalProcedureData[stealthAxisIdx].stealthCaseCoverage =
                    draftAddProcedureData.stealthAxisCaseCoverage;
            }
            if (
                typeof draftAddProcedureData?.stealthAxisAutoPilot === 'boolean'
            ) {
                additionalProcedureData[stealthAxisIdx].stealthAxisAutoPilot =
                    draftAddProcedureData.stealthAxisAutoPilot;
            }
            if (
                typeof draftAddProcedureData?.stealthAxisLiveAlign === 'boolean'
            ) {
                additionalProcedureData[stealthAxisIdx].stealthAxisLiveAlign =
                    draftAddProcedureData.stealthAxisLiveAlign;
            }
        }
    }

    if (typeof draftAddProcedureData.isNavigaton === 'boolean') {
        const navigationIdx = additionalProcedureData.findIndex(
            (x) =>
                x.description ===
                t(
                    'reportUsage.procedure.ProcedureViewContainer.additionalProcedureData.options.stealthNavigation'
                )
        );
        if (navigationIdx >= 0) {
            additionalProcedureData[navigationIdx].isSelected =
                draftAddProcedureData.isNavigaton;
        }
    }
    if (typeof draftAddProcedureData.isPowerease === 'boolean') {
        const powereaseIdx = additionalProcedureData.findIndex(
            (x) =>
                x.description ===
                t(
                    'reportUsage.procedure.ProcedureViewContainer.additionalProcedureData.options.powerease'
                )
        );
        if (powereaseIdx >= 0) {
            additionalProcedureData[powereaseIdx].isSelected =
                draftAddProcedureData.isPowerease;
        }
    }
    if (typeof draftAddProcedureData.isAdvancedEnergy === 'boolean') {
        const advancedEnergyIdx = additionalProcedureData.findIndex(
            (x) =>
                x.description ===
                t(
                    'reportUsage.procedure.ProcedureViewContainer.additionalProcedureData.options.advancedEnergy'
                )
        );
        if (advancedEnergyIdx >= 0) {
            const draftProduct = draftAddProcedureData?.advancedEnergyProduct;
            const existingProduct =
                additionalProcedureData?.[advancedEnergyIdx]
                    ?.advancedEnergyProduct;

            const areEqual =
                JSON.stringify(draftProduct) ===
                JSON.stringify(existingProduct);

            additionalProcedureData[advancedEnergyIdx].isSelected =
                draftAddProcedureData?.isAdvancedEnergy;

            if (areEqual) {
                additionalProcedureData[
                    advancedEnergyIdx
                ].advancedEnergyProduct = draftProduct;
            } else {
                for (let key in existingProduct) {
                    if (draftProduct.hasOwnProperty(key)) {
                        existingProduct[key] = draftProduct[key];
                    }
                }
            }
        }
    }
    return additionalProcedureData;
};

export const modifyDraftProcedureDataResponse = (
    procedureInfoData,
    draftProcedureData
) => {
    if (
        draftProcedureData?.additionProcedure &&
        Object.keys(draftProcedureData?.additionProcedure).length > 0 &&
        procedureInfoData?.additionalProcedure
    ) {
        procedureInfoData.additionalProcedure = getAdditionalProcedureDraftData(
            draftProcedureData?.additionProcedure,
            procedureInfoData?.additionalProcedure
        );
    }
    if (
        draftProcedureData?.physicianFinderEligibility &&
        draftProcedureData?.physicianFinderEligibility.length > 0 &&
        procedureInfoData?.therapies
    ) {
        procedureInfoData.therapies = procedureInfoData?.therapies.map(
            (phyFinderObj) => ({
                ...phyFinderObj,
                isSelected:
                    draftProcedureData?.physicianFinderEligibility.includes(
                        phyFinderObj.code
                    )
                        ? true
                        : null,
            })
        );
    }
    if (
        draftProcedureData?.usedRetractors &&
        draftProcedureData?.usedRetractors.length > 0 &&
        procedureInfoData?.retractors
    ) {
        procedureInfoData.retractors = procedureInfoData?.retractors.map(
            (retractorsObj) => ({
                ...retractorsObj,
                isSelected: draftProcedureData?.usedRetractors.includes(
                    retractorsObj.code
                )
                    ? true
                    : null,
            })
        );
    }
    if (
        draftProcedureData?.usedUIV?.length > 0 ||
        draftProcedureData?.usedLIV?.length > 0
    ) {
        const normalizeLevelCode = (value) => {
            if (value === null || value === undefined) return '';
            if (typeof value === 'number') return String(value).padStart(2, '0');
            const str = String(value).trim();
            return /^\d+$/.test(str) && str.length === 1
                ? str.padStart(2, '0')
                : str;
        };

        const usedUIV = Array.isArray(draftProcedureData?.usedUIV)
            ? draftProcedureData.usedUIV
            : [];
        const usedLIV = Array.isArray(draftProcedureData?.usedLIV)
            ? draftProcedureData.usedLIV
            : [];

        const usedLevelCodes = new Set(
            [...usedUIV, ...usedLIV].map(normalizeLevelCode).filter(Boolean)
        );

        if (procedureInfoData?.levels?.length > 0) {
            procedureInfoData.levels = procedureInfoData.levels.map((level) =>
                usedLevelCodes.has(normalizeLevelCode(level.code))
                    ? { ...level, isSelected: true }
                    : level
            );
        }
    }
    if (
        draftProcedureData?.deformityCorrectionCode &&
        draftProcedureData?.deformityCorrectionCode.length > 0 &&
        procedureInfoData?.technologies
    ) {
        procedureInfoData.technologies = procedureInfoData?.technologies.map(
            (deformityObj) => ({
                ...deformityObj,
                isSelected:
                    draftProcedureData?.deformityCorrectionCode ===
                    deformityObj.code
                        ? deformityObj.description
                        : null,
            })
        );
    }
    if (
        draftProcedureData?.biologicsUsed &&
        draftProcedureData?.biologicsUsed.length > 0 &&
        procedureInfoData?.biologicsUsed
    ) {
        procedureInfoData.biologicsUsed = procedureInfoData?.biologicsUsed.map(
            (bioObj) => ({
                ...bioObj,
                isSelected: draftProcedureData?.biologicsUsed.includes(
                    bioObj.code
                )
                    ? true
                    : null,
            })
        );
    }
    if (
        draftProcedureData?.medtronicSupp &&
        draftProcedureData?.medtronicSupp.length > 0 &&
        procedureInfoData?.biologicsDetail
    ) {
        procedureInfoData.biologicsDetail =
            procedureInfoData?.biologicsDetail.map((data) => ({
                ...data,
                isMedtronicSelected: draftProcedureData?.medtronicSupp.includes(
                    data.code
                )
                    ? true
                    : null,
            }));
    }
    if (
        draftProcedureData?.compatitiveSupp &&
        draftProcedureData?.compatitiveSupp.length > 0 &&
        procedureInfoData?.biologicsDetail
    ) {
        procedureInfoData.biologicsDetail =
            procedureInfoData?.biologicsDetail.map((data) => ({
                ...data,
                isCompetitiveSelected:
                    draftProcedureData?.compatitiveSupp.includes(data.code)
                        ? true
                        : null,
            }));
    }
    return procedureInfoData;
};

export const getDraftWhyAllRodsNotImplanted = (
    whyAllRodsNotImplanted,
    draftUnidInfo
) => {
    return whyAllRodsNotImplanted.map((item) => {
        const updatedItem = { ...item };
        if (updatedItem.id === 1) {
            updatedItem.isChecked = draftUnidInfo.isSurgeonChangedSurgicalPlan;
        }
        if (updatedItem.id === 2) {
            updatedItem.isChecked = draftUnidInfo.isIssueWithLengthOrContour;
        }
        if (updatedItem.id === 3) {
            updatedItem.isChecked = draftUnidInfo.isSalesOperations;
        }
        if (updatedItem.id === 4) {
            updatedItem.isChecked = draftUnidInfo.isManufacturingIssue;
        }
        if (updatedItem.id === 5) {
            updatedItem.isChecked = draftUnidInfo.isNotDeliveredOnTime;
        }
        if (updatedItem.id === 6) {
            updatedItem.isChecked = draftUnidInfo.isMultipleVersions;
        }
        return updatedItem;
    });
};

export const getDraftWhyNotImplantedAsMainRod = (
    whyNotImplantedAsMainRod,
    draftUnidInfo
) => {
    return whyNotImplantedAsMainRod.map((item) => {
        const updatedItem = { ...item };
        if (updatedItem.id === 1) {
            updatedItem.isChecked = draftUnidInfo.asMainRodSurgicalPlan
                ? draftUnidInfo.asMainRodSurgicalPlan
                : false;
        }
        if (updatedItem.id === 2) {
            updatedItem.isChecked = draftUnidInfo.asMainRodLengthOrContour
                ? draftUnidInfo.asMainRodLengthOrContour
                : false;
        }
        return updatedItem;
    });
};

export const getDraftMainRodCount = (draftUnidInfo) => {
    if (
        draftUnidInfo.unidImplanteditems &&
        draftUnidInfo.unidImplanteditems.length > 0
    ) {
        let items = [...draftUnidInfo.unidImplanteditems];
        items = items.filter((data) => data.isMainRod);
        return items && items.length;
    } else {
        return 0;
    }
};
// eslint-disable-next-line no-unused-vars
export const convertDataRpToRecord = (rpDetaiils, filteredProduct) => {
    let newArray = [];
    rpDetaiils.map((item) => {
        if (item.isAdditionalCartEntry) {
            let newObject = {
                basePrice: item.basePrice,
                description: item.description,
                isLot: item.isLot,
                isSerialRequired: item.isSerialRequired,
                isSet: item.isSet,
                materialType: item.materialType,
                productCFN: item.productCFN,
                salesOrg: item.salesOrg,
                uom: item.uom,
                cfn: item.cfn,
                lotSerial: [
                    {
                        used:
                            item.salesOrg === 'S030'||item?.showCheckBox&&item.usedQuantity
                                ? item.usedQuantity === '1'
                                : item.usedQuantity,
                        id: getUniqueKey(),
                        Serial: item.lotSerialNumber,
                        isInvalidQtyMsg: null,
                        other: null,
                    },
                ],
                accountOwned: item.accountOwned,
                isNewProduct: true,
                extPrice: null,
                otherQtyExtPrice: null,
                usedQtyExtPrice: null,
                isInvalidItemMsg: null,
                isAdditionalCartEntry: true,
            };
            if (item.payment) {
                newObject.payment = item.payment;
            }
            newArray.push(newObject);
        }
        else {
            newArray.push({
                ...item,
        });        }
    });

    return newArray;
};
export const getLotSerialInputPlaceHolder = (item,lotItem) => {
    if (lotItem===t('reportUsage.unknown')?item?.lotSerialUnknown:lotItem?.lotSerialUnknown)
        return t('reportUsage.unknown');
    if (isS33Item(item)) return 'CaseID';
    if (item.isBundleItem)
        return t(
            'reportUsage.review.itemsToBeBilled.ItemsToBeBilled.notApplicable'
        );
    else if (
        (item.isSerialRequired && !item.isLot) ||
        (item.isSerialRequired && item.isLot && item.salesOrg === 'S030') ||
        (item.isLotSerialReqd && item.salesOrg === 'S030')
    )
        return t('reportUsage.itemList.lotSerialInputPlaceHolder.serialReq');
    else if (
        (item.isLot && item.isSerialRequired) ||
        (item.isLotSerialReqd && item.salesOrg === 'S002')
    )
        return t('reportUsage.itemList.lotSerialInputPlaceHolder.lotReq');
    else if (item.salesOrg === 'S030' && item.isSerialRequired === false)
        return t('reportUsage.itemList.lotSerialInputPlaceHolder.serialOpt');
    else if (
        !item.salesOrg ||
        item.salesOrg === '' ||
        (!Object.prototype.hasOwnProperty.call(item, 'isSerialRequired') &&
            !Object.prototype.hasOwnProperty.call(item, 'isLot'))
    )
        return t('reportUsage.itemList.lotSerialInputPlaceHolder.lotOrSerial');
    else {
        const w = Math.min(window.innerWidth, window.innerHeight);
        const h = Math.max(window.innerWidth, window.innerHeight);
        if (((w > 768 && w <= 820) && (h > 1024 && h <= 1180)) || (w == 1024 && h == 1366)) {
            return t('reportUsage.itemList.lotSerialInputPlaceHolder.Optional');
        } else {
            return t('reportUsage.itemList.lotSerialInputPlaceHolder.lotOpt');
        }
    }
};
export const checkDescrepencyExistsInArrays = (
    usageArray,
    usageDescrepencyExists
) => {
    let usageProducts = Array.from(
        new Set(usageArray.map((item) => item?.productCFN))
    ).map((x) => {
        return {
            cfn: x,
            usedQty: 0,
            otherQty: 0,
            accountOwned: 0,
        };
    });

    if (usageDescrepencyExists) {
        usageArray.map((item) => {
            const getItemIndex = usageProducts?.findIndex(
                (x) => x?.cfn === item?.productCFN
            );
            if (getItemIndex >= 0) {
                const updateUniqueProducts = usageProducts[getItemIndex];
                updateUniqueProducts.usedQty =
                    Number(updateUniqueProducts?.usedQty) +
                    Number(item?.usedQuantity ? item?.usedQuantity : 0);
                updateUniqueProducts.otherQty =
                    Number(updateUniqueProducts.otherQty) +
                    Number(item?.otherQuantity ? item?.otherQuantity : 0);
                updateUniqueProducts.accountOwned =
                    Number(updateUniqueProducts.accountOwned) +
                    Number(item?.isAccountOwned === true ? 1 : 0);
                usageProducts[getItemIndex] = updateUniqueProducts;
            }
        });
    }
    return usageProducts;
};

export const setShippingDetailsRP = (
    reportPaymentGridData,
    replenRPGridData
) => {
    let newArray = [];
    reportPaymentGridData.map((item) => {
        if (
            item.isAdditionalCartEntry &&
            item.isReplenishmentRequired &&
            item.salesOrg !== 'S030' &&
            !item?.cfn?.startsWith('B33')
        ) {
            if (item.isBundleItem) {
                item.bundleItem.map((child, i) => {
                    if (child.isAdditionalCartChild) {
                        replenRPGridData.map((replenChild) => {
                            if (
                                replenChild.productCFN === child.productCFN
                                // &&
                                // replenChild.id === child.id
                            ) {
                                let newObject = replenChild?.shippingDetails
                                    ?.accountDetails?.accountId
                                    ? {
                                          childReplenDetails: {
                                              replenishQuantity:
                                                  replenChild.newReplenQty
                                                      ? replenChild.newReplenQty
                                                      : replenChild.replenQty,
                                              accountId:
                                                  replenChild?.shippingDetails
                                                      ?.accountDetails
                                                      ?.accountId,
                                              shipTo: replenChild
                                                  ?.shippingDetails
                                                  ?.shippingAddress
                                                  ?.shippingAddressId,
                                              shippingMethodId:
                                                  replenChild?.shippingDetails
                                                      ?.shippingMethod
                                                      ?.shippingMethodId,
                                              attentionLine:
                                                  replenChild?.shippingDetails
                                                      ?.attnLine,
                                              requestDeliveryDate:
                                                  replenChild?.shippingDetails
                                                      ?.requestDeliveryDate,
                                              surgeryDate:
                                                  replenChild?.shippingDetails
                                                      ?.surgeryDate,
                                              surgeryNumber:
                                                  replenChild?.shippingDetails
                                                      ?.surgeryNumber,
                                                       billToId: replenChild?.shippingDetails?.billToId,
                                    careOfField: replenChild?.shippingDetails?.careOfField,
                                    transferRecipient: replenChild?.shippingDetails?.transferRecipient?.addressId,
                                          },
                                          ...child,
                                          isReplenish: true,
                                          replenishmentDetails: {
                                              replenishQuantity:
                                                  replenChild.newReplenQty
                                                      ? replenChild.newReplenQty
                                                      : replenChild.replenQty,
                                              accountId:
                                                  replenChild?.shippingDetails
                                                      ?.accountDetails
                                                      ?.accountId,
                                              shipTo: replenChild
                                                  ?.shippingDetails
                                                  ?.shippingAddress
                                                  ?.shippingAddressId,
                                              shippingMethodId:
                                                  replenChild?.shippingDetails
                                                      ?.shippingMethod
                                                      ?.shippingMethodId,
                                              attentionLine:
                                                  replenChild?.shippingDetails
                                                      ?.attnLine,
                                              requestDeliveryDate:
                                                  replenChild?.shippingDetails
                                                      ?.requestDeliveryDate,
                                              surgeryDate:
                                                  replenChild?.shippingDetails
                                                      ?.surgeryDate,
                                              surgeryNumber:
                                                  replenChild?.shippingDetails
                                                      ?.surgeryNumber,

                                    billToId: replenChild?.shippingDetails?.billToId,
                                    careOfField: replenChild?.shippingDetails?.careOfField,
                                    transferRecipient: replenChild?.shippingDetails?.transferRecipient?.addressId,
                                          },
                                      }
                                    : {
                                          ...child,
                                          isReplenish: false,
                                      };
                                item.bundleItem[i] = newObject;
                            }
                        });
                    }
                });
                newArray.push(item);
            }
            replenRPGridData.map((data) => {
                if (
                    data.cfn === item.cfn
                    // && data?.id === item?.id
                ) {
                    let newObject = data?.shippingDetails?.accountDetails
                        ?.accountId
                        ? {
                              ...item,
                              isReplenishmentRequired: data.isReplenishmentRequired,
                              isReplenish: true,
                              replenishmentDetails: {
                                  replenishQuantity: data?.newReplenQty
                                      ? data?.newReplenQty
                                      : data?.replenQty,
                                  accountId:
                                      data?.shippingDetails?.accountDetails
                                          ?.accountId,
                                  shipTo: data?.shippingDetails?.shippingAddress
                                      ?.shippingAddressId,
                                  shippingMethodId:
                                      data?.shippingDetails?.shippingMethod
                                          ?.shippingMethodId,
                                  attentionLine:
                                      data?.shippingDetails?.attnLine,
                                  requestDeliveryDate:
                                      data?.shippingDetails
                                          ?.requestDeliveryDate,
                                  surgeryDate:
                                      data?.shippingDetails?.surgeryDate,
                                  surgeryNumber:
                                      data?.shippingDetails?.surgeryNumber,
                                    billToId: data?.shippingDetails?.billToId,
                                    careOfField: data?.shippingDetails?.careOfField,
                                    transferRecipient: data?.shippingDetails?.transferRecipient?.addressId,
                              },
                          }
                        : {
                              ...item,
                              isReplenishmentRequired: data?.isReplenishmentRequired,
                              isReplenish: false,
                          };
                    newArray.push(newObject);
                }
            });
        } else {
            let newObject = {
                ...item,
                isReplenishmentRequired: item?.isReplenishmentRequired,
                isReplenish: false,
            };
            newArray.push(newObject);
        }
    });

    return newArray;
};

export const deletePricingDetails = (products) => {
    return products.map((item) => {
        delete item.basePrice;
        delete item.currencyISOCode;
        item.extPrice = '';
        delete item.otherQtyExtPrice;
        delete item.usedQtyExtPrice;
        delete item.adjustedUsedQtyPrice;
        delete item.adjustedOtherQtyPrice;
        delete item.isOverrideApplied;
        delete item.listPrice;
        delete item.shippingDetails;
        return item;
    });
};

//checking quantity of item list
export const isQtyPresent = (array) => {
    // Check if basePrice is present in all objects
    const allHaveBasePrice = array?.every((obj) => obj?.basePrice);

    if (!allHaveBasePrice) {
        return false;
    }

    // Check for invalid quantity or lot serial messages
    const hasInvalidMsg = array?.some(
        (obj) =>
            (getArrayItem(obj?.lotSerial)?.isInvalidQtyMsg !== null &&
                getArrayItem(obj?.lotSerial)?.isInvalidQtyMsg !== undefined) ||
            (getArrayItem(obj?.lotSerial)?.isInvalidLotSerialMsg !== null &&
                getArrayItem(obj?.lotSerial)?.isInvalidLotSerialMsg !== undefined &&
                getArrayItem(obj?.lotSerial)?.isInvalidLotSerialMsg !== false)
    );

    return hasInvalidMsg ? 'invalidQty' : true;
};
//for lotserial number validation
export const isValidLotSerial = (data) => {
    return data.every((item) => {
        let required = item.isSerialRequired || item.isLotSerialReqd;

        return item.lotSerial.every((lotSerial) => {
            return (
                (lotSerial.used > 0 || lotSerial.other > 0) &&
                (lotSerial.isInvalidLotSerialMsg === null ||
                    lotSerial.isInvalidLotSerialMsg === undefined) &&
                (lotSerial.isInvalidQtyMsg === null ||
                    lotSerial.isInvalidQtyMsg === undefined) &&
                (required ? lotSerial.Serial !== '' : true)
            );
        });
    });
};

//setting already replenished products RDD (if multiple products has different RDD,then it will show latest RDD in datepicker)
export const getRequestDeliveryDateForReplenish = (
    data,
    productCFN,
    isOutSide,
    accountId
) => {
    const productCfn = productCFN;
    let date, product, firstDate, account, teritorry;
    teritorry = data.find((item) => {
        return (
            productCfn.includes(item.cfn) && item?.shippingDetails?.isOutSide
        );
    })?.shippingDetails?.isOutSide;

    account = data.find((item) => {
        return (
            productCfn.includes(item.cfn) &&
            item?.shippingDetails?.accountDetails?.accountId === accountId
        );
    })?.shippingDetails?.accountDetails?.accountId;

    if (account && account === accountId) {
        product = data
            .filter(
                (item) =>
                    productCfn.includes(item.cfn) &&
                    item?.shippingDetails?.requestDeliveryDate
            )
            .map((item) => new Date(item.shippingDetails.requestDeliveryDate));

        if (product.length > 1) {
            firstDate = new Date(getArrayItem(product)).setHours(0, 0, 0, 0);
            const allSame = product.every(
                (item) => item.setHours(0, 0, 0, 0) === firstDate
            );
            return allSame
                ? teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false
                : false;
        } else {
            date =
                teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false;
        }

        return date;
    } else {
        return false;
    }
};

export const getSurgeryDateForReplenish = (
    data,
    productCFN,
    isOutSide,
    accountId
) => {
    const productCfn = productCFN;
    let date, product, firstDate, account, teritorry;
    teritorry = data.find((item) => {
        return (
            productCfn.includes(item.cfn) && item?.shippingDetails?.isOutSide
        );
    })?.shippingDetails?.isOutSide;

    account = data.find((item) => {
        return (
            productCfn.includes(item.cfn) &&
            item?.shippingDetails?.accountDetails?.accountId === accountId
        );
    })?.shippingDetails?.accountDetails?.accountId;

    if (account && account === accountId) {
        product = data
            .filter(
                (item) =>
                    productCfn.includes(item.cfn) &&
                    item?.shippingDetails?.surgeryDate
            )
            .map((item) => new Date(item.shippingDetails.surgeryDate));

        if (product.length > 1) {
            firstDate = new Date(getArrayItem(product)).setHours(0, 0, 0, 0);
            const allSame = product.every(
                (item) => item.setHours(0, 0, 0, 0) === firstDate
            );
            return allSame
                ? teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false
                : false;
        } else {
            date =
                teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false;
        }

        return date;
    } else {
        return false;
    }
};

export const getCareOfFieldForReplenish = (
    data,
    productCFN,
    isOutSide,
    accountId
) => {
    const productCfn = productCFN;
    let date, product, firstDate, account, teritorry;
    teritorry = data.find((item) => {
        return (
            productCfn.includes(item.cfn) && item?.shippingDetails?.isOutSide
        );
    })?.shippingDetails?.isOutSide;

    account = data.find((item) => {
        return (
            productCfn.includes(item.cfn) &&
            item?.shippingDetails?.accountDetails?.accountId === accountId
        );
    })?.shippingDetails?.accountDetails?.accountId;

    if (account && account === accountId) {
        product = data
            .filter(
                (item) =>
                    productCfn.includes(item.cfn) &&
                    item?.shippingDetails?.careOfField
            )
            .map((item) => item.shippingDetails?.careOfField);

        if (product.length > 1) {
            firstDate = getArrayItem(product);
            const allSame = product.every((item) => item === firstDate);
            return allSame
                ? teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false
                : false;
        } else {
            date =
                teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false;
        }

        return date;
    } else {
        return false;
    }
};
export const getTransferRecipientForReplenish = (
    data,
    productCFN,
    isOutSide,
    accountId,
    isFromConsigmentBilling
) => {
    const productCfn = productCFN;
    let date, product, firstDate, account, teritorry;
    teritorry = data.find((item) => {
        return (
            productCfn.includes(item.cfn) && item?.shippingDetails?.isOutSide
        );
    })?.shippingDetails?.isOutSide;

    account = data.find((item) => {
        return (
            productCfn.includes(item.cfn) &&
            item?.shippingDetails?.accountDetails?.accountId === accountId
        );
    })?.shippingDetails?.accountDetails?.accountId;

    if (account && account === accountId) {
        product = data
            .filter(
                (item) =>
                    productCfn.includes(item.cfn) &&
                    item?.shippingDetails?.transferRecipient
            )
            .map((item) => isFromConsigmentBilling?item.shippingDetails?.transferRecipientObj:item.shippingDetails?.transferRecipient);

        if (product.length > 1) {
            firstDate = isFromConsigmentBilling?getArrayItem(product)?.transferRecipientObj:getArrayItem(product);
            const allSame = product.every((item) => (isFromConsigmentBilling?item?.transferRecipientObj:item) === firstDate);
            return allSame
                ? teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false
                : false;
        } else {
            date =
                teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false;
        }

        return date;
    } else {
        return false;
    }
};
export const getShipToAddressForReplenish = (
    data,
    productCFN,
    isOutSide,
    accountId
) => {
    const productCfn = productCFN;
    let date, product, firstDate, account, teritorry;
    teritorry = data.find((item) => {
        return (
            productCfn.includes(item.cfn) && item?.shippingDetails?.isOutSide
        );
    })?.shippingDetails?.isOutSide;

    account = data.find((item) => {
        return (
            productCfn.includes(item.cfn) &&
            item?.shippingDetails?.accountDetails?.accountId === accountId
        );
    })?.shippingDetails?.accountDetails?.accountId;

    if (account && account === accountId) {
        product = data
            .filter(
                (item) =>
                    productCfn.includes(item.cfn) &&
                    item?.shippingDetails?.shippingAddress?.shippingAddressId
            )
            .map(
                (item) =>
                    item.shippingDetails?.shippingAddress?.shippingAddressId
            );

        if (product.length > 1) {
            firstDate = getArrayItem(product);
            const allSame = product.every((item) => item === firstDate);
            return allSame
                ? teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false
                : false;
        } else {
            date =
                teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false;
        }

        return date;
    } else {
        return false;
    }
};

export const getSurgeryNumberForReplenish = (
    data,
    productCFN,
    isOutSide,
    accountId
) => {
    const productCfn = productCFN;
    let date, product, firstSurgeryNumber, teritorry;
    teritorry = data.find((item) => {
        return (
            productCfn.includes(item.cfn) && item?.shippingDetails?.isOutSide
        );
    })?.shippingDetails?.isOutSide;

    product = data
        .filter(
            (item) =>
                productCfn.includes(item.cfn) &&
                item?.shippingDetails?.surgeryNumber
        )
        .map((item) => item.shippingDetails.surgeryNumber);

    if (product.length > 1) {
        firstSurgeryNumber = getArrayItem(product);
        const allSame = product.every((item) => item === firstSurgeryNumber);
        return allSame
            ? teritorry && isOutSide
                ? getArrayItem(product)
                : !teritorry && !isOutSide
                ? getArrayItem(product)
                : false
            : false;
    } else {
        date =
            teritorry && isOutSide
                ? getArrayItem(product)
                : !teritorry && !isOutSide
                ? getArrayItem(product)
                : false;
    }

    return date;
};

export const getShippingMethodReplenish = (
    data,
    productCFN,
    isOutSide,
    accountId,
    isFromConsighnment
) => {
    const productCfn = productCFN;
    let date, product, firstDate, account, teritorry;
    teritorry = data.find((item) => {
        return (
            productCfn.includes(item.cfn) && item?.shippingDetails?.isOutSide
        );
    })?.shippingDetails?.isOutSide;
 
    account = data.find((item) => {
        return (
            productCfn.includes(item.cfn) &&
            item?.shippingDetails?.accountDetails?.accountId === accountId
        );
    })?.shippingDetails?.accountDetails?.accountId;
 
    if (account && account === accountId) {
        product = data
            .filter(
                (item) =>
                    productCfn.includes(item.cfn) &&
                    item?.shippingDetails?.shippingMethod
            )
            .map((item) => isFromConsighnment?item.shippingDetails:item.shippingDetails?.shippingMethod);
 
        if (product.length > 1) {
            firstDate = isFromConsighnment?getArrayItem(product)?.shippingMethod:getArrayItem(product);
            const allSame = product.every((item) => {
                const currentId = isFromConsighnment ? item?.shippingMethod?.shippingMethodId : item?.shippingMethodId;
                const firstId = isFromConsighnment ? firstDate?.shippingMethodId : firstDate?.shippingMethodId;
                return currentId === firstId;
            });
            return allSame
                ? teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false
                : false;
        } else {
            date =
                teritorry && isOutSide
                    ? getArrayItem(product)
                    : !teritorry && !isOutSide
                    ? getArrayItem(product)
                    : false;
        }
 
        return date;
    } else {
        return false;
    }
};
